import { supabase, forceReconnect } from '../lib/supabase';

interface Lead {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  source: string;
  rating?: string;
  latitude?: number;
  longitude?: number;
  website?: string;
  hasRealPhone?: boolean;
  hasRealEmail?: boolean;
}

interface LeadsResult {
  success: boolean;
  leads: Lead[];
  error?: string;
  executionTime: number;
  sources: string[];
  message?: string;
  fallbackUsed?: boolean;
  originalSearch?: {
    businessType: string;
    location: string;
  };
  suggestions?: AlternativeSearch[];
  fromCache?: boolean; // ✅ ADICIONAR esta linha
}

// ✅ Adicionar esta nova interface
interface AlternativeSearch {
  businessType: string;
  location: string;
  displayText: string;
}

interface OSMElement {
  type: string;
  id: number;
  lat?: number;
  lon?: number;
  center?: {
    lat: number;
    lon: number;
  };
  tags?: {
    [key: string]: string;
  };
}

// Adicione esta interface no início do arquivo, se ainda não existir
interface FallbackContact {
  id: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  source: string;
  rating?: string;
  latitude?: number;
  longitude?: number;
  website?: string;
  hasRealPhone?: boolean;
  hasRealEmail?: boolean;
}

// ✅ Adicione esta interface para o cache (antes da const CACHE_EXPIRATION_TIME)
interface CacheEntry {
  allLeads: Lead[];          // Todos os resultados (27)
  sources: string[];
  executionTime: number;
  message?: string;
  fallbackUsed?: boolean;
  originalSearch?: {
    businessType: string;
    location: string;
  };
  suggestions?: AlternativeSearch[];
}

// Lista completa de servidores prioritários
const OVERPASS_SERVERS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
  'https://overpass.openstreetmap.fr/api/interpreter',
  'https://overpass.osm.brasil.xyz/api/interpreter',
  'https://overpass.nchc.org.tw/api/interpreter',
  'https://overpass.osm.ch/api/interpreter',
  'https://overpass.osm.be/api/interpreter',
  'https://overpass.zibi.me/api/interpreter',
  'https://overpass.openstreetmap.ru/api/interpreter',
  'https://overpass.private.coffee/api/interpreter',
  'https://overpass.osm.jp/api/interpreter',
  'https://overpass.osm.rambler.ru/cgi/interpreter',
  'https://gall.openstreetmap.de/api/interpreter',
  'https://lambert.openstreetmap.de/api/interpreter'
];

const createCacheKey = (searchTerm: string, cityName?: string): string => {
  const normalizedSearch = searchTerm.toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/\s+/g, "-");
  
  const normalizedCity = cityName 
    ? cityName.toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "-")
    : "default";
  
  return `${normalizedSearch}-${normalizedCity}`;
};

export const clearSearchCache = (searchTerm?: string, cityName?: string): void => {
  if (searchTerm && cityName) {
    const cacheKey = createCacheKey(searchTerm, cityName);
    cache.delete(cacheKey);
    console.log(`🧹 Cache limpo para: ${cacheKey}`);
  } else {
    // Limpa todo o cache
    cache.clear();
    console.log('🧹 Todo o cache foi limpo');
  }
};

const tryWithAllServers = async (
  query: { query: string; name: string },
  businessType: string,
  location: string,
  estado?: string
): Promise<Lead[]> => {
  const errors = [];
  
  // Tenta cada servidor em ordem
  for (const server of OVERPASS_SERVERS) {
    try {
      console.log(`🔄 Tentando no servidor ${server}`);
      const response = await fetchWithTimeout(server, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `data=${encodeURIComponent(query.query)}`
      }, 15000); // 15 segundos de timeout

      if (!response.ok) {
        throw new Error(`HTTP status ${response.status}`);
      }

      const data = await response.json();
      if (data.elements?.length > 0) {
        console.log(`✅ Sucesso no servidor ${server} com ${data.elements.length} resultados`);
        return processOSMElements(data.elements, businessType, location, estado);
      }
      return []; // Nenhum resultado encontrado
      
    } catch (error) {
      console.error(`❌ Falha no servidor ${server}:`, error);
      errors.push(error);
      
      // Penaliza o servidor que falhou
      serverUsageMap.set(server, 9999);
      lastServerUsage.set(server, Date.now());
      
      // Pequena pausa entre tentativas
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }
  
  throw new AggregateError(errors, 'Todos os servidores falharam');
};

const tryAllStrategies = async (
  businessType: string,
  location: string,
  estado?: string
): Promise<Lead[]> => {
  const amenityTags = getAmenityTags(businessType);
  
  // 1. Busca Principal
  console.log('1️⃣ Busca principal na área exata');
  let results = await tryWithAllServers(
    buildMainQuery(businessType, location, amenityTags, estado),
    businessType,
    location,
    estado
  );
  if (results.length > 0) return results;

  // 2. Busca por Nome da Localidade
  console.log('2️⃣ Busca por nome da localidade');
  results = await tryWithAllServers(
    {
      query: `[out:json][timeout:25];
        (
          node["name"~"${location}",i][~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];          
        );
        out body center qt 1000;`,
      name: `Busca por nome contendo "${location}"`
    },
    businessType,
    location,
    estado
  );
  if (results.length > 0) return results;

  // 3. Busca em Cidades Vizinhas
  console.log('3️⃣ Busca em cidades vizinhas');
  return tryWithAllServers(
    {
      query: `[out:json][timeout:300];
        (
          rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
          node["name"="${location}"]["place"="village"];
        );
        map_to_area -> .main_area;
        (
          rel(around.main_area:20000)["type"="boundary"]["admin_level"="8"];
        );
        map_to_area -> .neighbor_areas;
        (
          node(area.neighbor_areas)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
          way(area.neighbor_areas)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
        );
        out body center qt 1000;`,
      name: `Busca em cidades próximas a ${location}`
    },
    businessType,
    location,
    estado
  );
};

const buildVillageSearchQuery = (location: string, businessType: string, amenityTags: string[]): { query: string; name: string }[] => {
  const queries = [];

  // 1. PRIMEIRA TENTATIVA: Busca por boundary administrativo (distrito)
  queries.push({
    query: `[out:json][timeout:300];
// Busca por boundary do distrito
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
);
map_to_area -> .search_area;

// Busca estabelecimentos na área
(
  ${amenityTags.map(tag => `
    node(area.search_area)[${tag.includes('=') ? tag : `amenity=${tag}`}];
    way(area.search_area)[${tag.includes('=') ? tag : `amenity=${tag}`}];
  `).join('')}
);
out body center qt 1000;`,
    name: `Busca por boundary administrativo em ${location}`
  });

  // 2. SEGUNDA TENTATIVA: Busca por place=village com buffer
  queries.push({
    query: `[out:json][timeout:300];
// Busca o nó central da vila
node["name"="${location}"]["place"="village"];
out center qt;

// Cria buffer de 5km ao redor
(
  way(around:5000)["boundary"="administrative"]["admin_level"="8"];
  relation(around:5000)["boundary"="administrative"]["admin_level"="8"];
);
map_to_area -> .buffer_area;

// Busca estabelecimentos no buffer
(
  ${amenityTags.map(tag => `
    node(area.buffer_area)[${tag.includes('=') ? tag : `amenity=${tag}`}];
    way(area.buffer_area)[${tag.includes('=') ? tag : `amenity=${tag}`}];
  `).join('')}
);
out body center qt 1000;`,
    name: `Busca com buffer ao redor de ${location}`
  });

  // 3. TERCEIRA TENTATIVA: Busca por nome em qualquer lugar
  queries.push({
    query: `[out:json][timeout:25];
// Busca estabelecimentos com nome da vila ou tipo de negócio
(
  node["name"~"${location}",i][~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  way["name"~"${location}",i][~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  relation["name"~"${location}",i][~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
);
out body center qt 1000;`,
    name: `Busca por estabelecimentos nomeados como ${location}`
  });

  // 4. QUARTA TENTATIVA: Busca em áreas administrativas vizinhas
  queries.push({
    query: `[out:json][timeout:300];
// Encontra a área principal
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  node["name"="${location}"]["place"="village"];
);
map_to_area -> .main_area;

// Encontra áreas vizinhas no mesmo nível administrativo
(
  rel(around.main_area:5000)["type"="boundary"]["admin_level"="8"];
);
map_to_area -> .neighbor_areas;

// Busca estabelecimentos nas áreas vizinhas
(
  ${amenityTags.map(tag => `
    node(area.neighbor_areas)[${tag.includes('=') ? tag : `amenity=${tag}`}];
    way(area.neighbor_areas)[${tag.includes('=') ? tag : `amenity=${tag}`}];
  `).join('')}
);
out body center qt 1000;`,
    name: `Busca em áreas vizinhas a ${location}`
  });

  return queries;
};

// Adicione esta constante com os 50 contatos de fallback
const BRAZIL_FALLBACK_CONTACTS: FallbackContact[] = [
  {
    id: "fallback-1",
    name: "Hospital Municipal Irmã Fanny Duran",
    phone: "+55 62 3935 1038",
    email: "saude@goianesia.go.gov.br",
    address: "Rua 33, 416, Centro, Goianésia - GO",
    source: "Fallback Database",
    rating: "4.5",
    latitude: -15.3298128,
    longitude: -49.1154888,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-2",
    name: "Adega Suíça Winestore",
    phone: "+55 62 3091 5770",
    email: "contato@adegasuica.com",
    address: "Avenida C-233, 665, Nova Suíça, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -16.7122607,
    longitude: -49.2755634,
    website: "http://www.adegasuica.com",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-3",
    name: "Hospital Municipal Doutor Evaristo Vilela Machado",
    phone: "+55 64 661 4277",
    email: "saude@mineiros.go.gov.br",
    address: "Rua Theodora Joana Cristina Michels, Boa Vista, Mineiros - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -17.5864600,
    longitude: -52.5587582,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-4",
    name: "Auto Peças e Mecânica JF",
    phone: "+55 62 995754747",
    email: "automjf@gmail.com",
    address: "Avenida Marechal Gouveia, Quadra: 34, Lote: 450, Anápolis - GO",
    source: "Fallback Database",
    rating: "4.0",
    latitude: -16.2834231,
    longitude: -48.9684390,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-5",
    name: "Plotte Fácil - Impressões",
    phone: "+55 62 4101-8877",
    email: "plottefacil@gmail.com",
    address: "Avenida T-9, 1082 - Apto 102, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.1",
    latitude: -16.7019188,
    longitude: -49.2724715,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-6",
    name: "Orto Mais Odontologia",
    phone: "+55 66 3401 5026",
    email: "ortomais@outlook.com",
    address: "Rua José André Varjão, 547, Barra do Garças - MT",
    source: "Fallback Database",
    rating: "4.4",
    latitude: -15.8751429,
    longitude: -52.2656890,
    website: "https://m.me/ortomaisbg",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-7",
    name: "Hospital Municipal Santa Efigênia Niquelândia",
    phone: "+55 62 354 2254",
    email: "niquelandia.saude@bol.com.br",
    address: "Avenida Anhanguera, 86, Niquelândia - GO",
    source: "Fallback Database",
    rating: "4.0",
    latitude: -14.4668476,
    longitude: -48.4583931,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-8",
    name: "Doce Mania Fest",
    phone: "+55 62 3323 1312",
    email: "docemania.distribuidora@hotmail.com",
    address: "Avenida Presidente Vargas, 50, centro, Ceres - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -15.3050923,
    longitude: -49.5972358,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-9",
    name: "Rossoni",
    phone: "+55 62 3313 1244",
    email: "contatos@gruporossoni.com.br",
    address: "Rua B, 1324, Anápolis - GO",
    source: "Fallback Database",
    rating: "4.1",
    latitude: -16.3449517,
    longitude: -48.9040775,
    website: "http://www.gruporossoni.com.br",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-10",
    name: "007 Oxigênio Gases industrais",
    phone: "+55 62 3247 0007",
    email: "oxigenio007@terra.com.br",
    address: "Avenida das Bandeiras, 710, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -16.6986516,
    longitude: -49.3152060,
    website: "http://www.oxigenio007.com.br",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-11",
    name: "Dr Ana Carolina Rocha",
    phone: "+55 62 3946 9090",
    email: "anacarolina@anacarolinarocha.com.br",
    address: "Rua 1129, 730, setor Marista, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.5",
    latitude: -16.7042270,
    longitude: -49.2626557,
    website: "https://anacarolinarocha.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-12",
    name: "Best Shop Baby",
    phone: "+55 27 9 9882 8623",
    email: "bestshopbaby@outlook.com",
    address: "Rua 1129, 730, setor Marista, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -16.7042322,
    longitude: -49.2626073,
    website: "https://bestshopbaby.com",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-13",
    name: "Hospital Municipal De Porto Alegre Do Norte",
    phone: "+55 65 569 1991",
    email: "sms.pan@yahoo.com.br",
    address: "Rua São Pedro, 288, Centro, Porto Alegre do Norte - MT",
    source: "Fallback Database",
    rating: "4.0",
    latitude: -10.8822715,
    longitude: -51.6334635,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-14",
    name: "Hospital Municipal",
    phone: "+55 64 3675 1271",
    email: "smspalminopolis@hotmail.com",
    address: "Rua Benjamin Gomes Pereira, Palminópolis - GO",
    source: "Fallback Database",
    rating: "4.1",
    latitude: -16.7935031,
    longitude: -50.1641974,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-15",
    name: "Ghost Informática",
    phone: "+55 62 3328-7601",
    email: "ghostxaps@gmail.com",
    address: "Rua Quintino Bocaiuva, 1201, Setor Central, Anápolis - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -16.3286455,
    longitude: -48.9594779,
    website: "https://www.ghostx.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-16",
    name: "Supertec Pneus",
    phone: "+55 62 3932-8900",
    email: "vendas@supertecpneus.com.br",
    address: "Avenida Perimetral, 4280, Setor Coimbra, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.4",
    latitude: -16.6795455,
    longitude: -49.2906197,
    website: "https://www.supertecpneus.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-17",
    name: "Atacadão",
    phone: "+55 62 4009-4710",
    email: "gerencia.gynbal@atacadaodiaadia.com.br",
    address: "Avenida Horácio Costa e Silva, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -16.6237143,
    longitude: -49.2858476,
    website: "https://www.atacadaodiaadia.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-18",
    name: "Blush Brand",
    phone: "+55 62 3996-0096",
    email: "contato@useblush.com.br",
    address: "Rua 1136, 251, Marista, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -16.6987960,
    longitude: -49.2581064,
    website: "https://www.useblush.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-19",
    name: "Degustatti",
    phone: "+556239200079",
    email: "degustatti@gmail.com",
    address: "Rua 22, 566, Setor Oeste, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.5",
    latitude: -16.6905546,
    longitude: -49.2697205,
    website: "https://lnk.bio/gtaF",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-20",
    name: "Hospital de Pequeno Porte de Alvorada",
    phone: "+55 63 3353 1839",
    email: "hppalvorada@gmail.com",
    address: "Avenida JK, 715, Centro, Alvorada - TO",
    source: "Fallback Database",
    rating: "4.0",
    latitude: -12.4790026,
    longitude: -49.1258048,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-21",
    name: "EMPORIUM MARAJO'S",
    phone: "+55 64 9 9644 9044",
    email: "lever.tino@hotmail.com",
    address: "Rua Marajos, 1010, Itumbiara - GO",
    source: "Fallback Database",
    rating: "4.1",
    latitude: -16.5226146,
    longitude: -50.3778032,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-22",
    name: "iFody Sex Shop Goiânia Delivery",
    phone: "+55 62 99164 1391",
    email: "contato@ifody.com.br",
    address: "Rua São Paulo, 630 - Quadra 6, Lote 2, Jardim Petropolis, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -16.6562802,
    longitude: -49.3409691,
    website: "https://ifodysexshop.com.br",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-23",
    name: "Point Hair - Apliques de Cabelos",
    phone: "(62)3212-3872",
    email: "crystianne1@live.com",
    address: "Rua 68, 211, Setor central, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -16.6683728,
    longitude: -49.2581330,
    website: "https://pointhair.visitemeu.site/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-24",
    name: "patricia oliveira",
    phone: "66992076348",
    email: "alexweigert@hotmail.com",
    address: "Rua 4, PQ SAO JORGE, RONDONOPOLIS - MT",
    source: "Fallback Database",
    rating: "4.0",
    latitude: -16.4823484,
    longitude: -54.5889910,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-25",
    name: "RP Motores",
    phone: "+55 62 4005-5500",
    email: "contato@rpmotores.com.br",
    address: "Avenida E-6, 100, Vila Lucy, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.4",
    latitude: -16.6957607,
    longitude: -49.3112867,
    website: "https://www.rpmotores.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-26",
    name: "Dutra Peças",
    phone: "556233483169",
    email: "lucianoautopecasdutra@gmail.com",
    address: "Avenida Enida Belem, 00, Vila Redençao, Rianapolis - GO",
    source: "Fallback Database",
    rating: "4.1",
    latitude: -15.4516862,
    longitude: -49.5037880,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-27",
    name: "Erva Fé Plantas Medicinais",
    phone: "+55 62 99232 7571",
    email: "joselopes61@gmail.com",
    address: "Rua São Bento, Corumbá de Goiás - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -15.9275580,
    longitude: -48.8057993,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-28",
    name: "R TECH Assistência & Importados",
    phone: "+55 62 99177 2916",
    email: "renatto.moreiiraa@gmail.com",
    address: "Avenida Engenheiro Roberto Müller, 450, Centro, Corumbá de Goiás - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -15.9263962,
    longitude: -48.8056025,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-29",
    name: "Clínica Cardioimagem",
    phone: "+55 62 3701 4020",
    email: "cardioimagem.anps@hotmail.com",
    address: "Avenida Senador José Lourenço Dias, 1077, Centro, Anápolis - GO",
    source: "Fallback Database",
    rating: "4.5",
    latitude: -16.3283362,
    longitude: -48.9511335,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-30",
    name: "Biomas - Cafés Especiais",
    phone: "+5562982511999",
    email: "gustavoalmeidacorretor@gmail.com",
    address: "Goiânia - GO",
    source: "Fallback Database",
    rating: "4.4",
    latitude: -16.6722694,
    longitude: -49.2538245,
    website: "http://biomascafes.com.br/",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-31",
    name: "IMOV Indústria Moveleira do Brasil",
    phone: "+55 62 98264 9454",
    email: "contato@imovbr.com.br",
    address: "Rua São João, Bairro São João, Quadra 46, Lote 06-07, Anápolis - GO",
    source: "Fallback Database",
    rating: "4.2",
    latitude: -16.3802473,
    longitude: -48.9619195,
    website: "https://veemenza.com.br",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-32",
    name: "Secretaria Municipal de Saúde",
    phone: "+55 62 3338 1939",
    email: "administracao@corumbadegoias.go.gov.br",
    address: "Avenida Brasília, 9 de Julho, Corumbá de Goiás - GO",
    source: "Fallback Database",
    rating: "4.0",
    latitude: -15.9193783,
    longitude: -48.8049402,
    website: "https://corumbadegoias.go.gov.br/estrutura-organizacional/?id=5",
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-33",
    name: "Unidade Básica de Saúde Viviam Parque",
    phone: "62 3387-5296",
    email: "vivianparquecadastro@gmail.com",
    address: "Avenida Pedro Ludovico, Qd 5, Lt 20, Viviam Parque, Anápolis - GO",
    source: "Fallback Database",
    rating: "4.1",
    latitude: -16.3869843,
    longitude: -48.9778445,
    hasRealPhone: true,
    hasRealEmail: true
  },
  {
    id: "fallback-34",
    name: "Tekoá Orgânicos e Sustentáveis",
    phone: "+55 62 99440-0505",
    email: "ademardequeiroz@gmail.com",
    address: "Rua 132 A, 94, Setor Sul, Goiânia - GO",
    source: "Fallback Database",
    rating: "4.3",
    latitude: -16.6958658,
    longitude: -49.2578482,
    website: "https://www.facebook.com/tekoaorganicos",
    hasRealPhone: true,
    hasRealEmail: true
  }
];

const CACHE_EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 horas
const cache = new Map<string, { data: CacheEntry; timestamp: number }>();
const searchHistory: Record<string, number> = {}; // Adicione esta linha
const serverUsageMap = new Map<string, number>();
const SERVER_COOLDOWN_TIME = 60 * 1000;
const lastServerUsage = new Map<string, number>();

// Função para embaralhar resultados com seed
const shuffleArray = (array: any[], seed: number): any[] => {
  let currentSeed = seed;
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const j = Math.floor(currentSeed / 233280 * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Função para rotacionar um array
const rotateArray = <T,>(array: T[], n: number): T[] => {
  if (array.length === 0) return array;
  const rotationIndex = n % array.length;
  return [...array.slice(rotationIndex), ...array.slice(0, rotationIndex)];
};

const getAmenityTags = (businessType: string): string[] => {
  const typeMap: { [key: string]: string[] } = {
    'restaurante': ['restaurant'],
    'restaurantes': ['restaurant'],
    'lanchonete': ['fast_food', 'restaurant'],
    'lanchonetes': ['fast_food', 'restaurant'],
    'padaria': ['bakery'],
    'padarias': ['bakery'],
    'pizzaria': ['restaurant'],
    'pizzarias': ['restaurant'],
    'bar': ['bar', 'pub'],
    'bares': ['bar', 'pub'],
    'cafe': ['cafe'],
    'cafes': ['cafe'],
    'sorveteria': ['ice_cream'],
    'sorveterias': ['ice_cream'],
    'hotel': ['hotel'],
    'hoteis': ['hotel'],
    'pousada': ['guest_house'],
    'pousadas': ['guest_house'],
    'posto': ['fuel'],
    'postos': ['fuel'],
    'farmacia': ['pharmacy'],
    'farmacias': ['pharmacy'],
    'banco': ['bank'],
    'bancos': ['bank'],
    'hospital': ['hospital'],
    'hospitais': ['hospital'],
    'clinica': ['clinic'],
    'clinicas': ['clinic'],
    'dentista': ['dentist'],
    'dentistas': ['dentist'],
    'veterinario': ['veterinary'],
    'veterinarios': ['veterinary'],
    'supermercado': ['supermarket'],
    'supermercados': ['supermarket'],
    'mercado': ['marketplace', 'supermarket'],
    'mercados': ['marketplace', 'supermarket'],
    'loja': ['shop'],
    'lojas': ['shop'],
    'shopping': ['mall'],
    'shoppings': ['mall'],
    'advocacia': ['lawyer'],
    'advogado': ['lawyer'],
    'advogados': ['lawyer'],
    'corretor': ['real_estate'],
    'corretores': ['real_estate'],
    'corretor de imoveis': ['real_estate'],
    'corretores de imoveis': ['real_estate'],
    'imobiliaria': ['real_estate'],
    'imobiliarias': ['real_estate'],
    'contador': ['accountant'],
    'contadores': ['accountant'],
    'contabilidade': ['accountant'],
    'arquiteto': ['architect'],
    'arquitetos': ['architect'],
    'engenheiro': ['engineer'],
    'engenheiros': ['engineer'],
    'medico': ['doctors'],
    'medicos': ['doctors'],
    'fisioterapeuta': ['physiotherapist'],
    'fisioterapeutas': ['physiotherapist'],
    'psicologo': ['psychologist'],
    'psicologos': ['psychologist'],
    'salao': ['beauty_salon', 'hairdresser'],
    'saloes': ['beauty_salon', 'hairdresser'],
    'cabeleireiro': ['hairdresser'],
    'cabeleireiros': ['hairdresser'],
    'manicure': ['beauty_salon', 'nail_salon'],
    'manicures': ['beauty_salon', 'nail_salon'],
    'estetica': ['beauty_salon'],
    'barbeiro': ['barber'],
    'barbearia': ['barber'],
    'barbearias': ['barber'],
    'oficina': ['car_repair'],
    'oficinas': ['car_repair'],
    'mecanico': ['car_repair'],
    'mecanicos': ['car_repair'],
    'lava-jato': ['car_wash'],
    'lava jato': ['car_wash'],
    'concessionaria': ['car_dealer'],
    'concessionarias': ['car_dealer'],
    'escola': ['school'],
    'escolas': ['school'],
    'universidade': ['university'],
    'universidades': ['university'],
    'faculdade': ['college'],
    'faculdades': ['college'],
    'curso': ['training'],
    'cursos': ['training'],
    'igreja': ['place_of_worship'],
    'igrejas': ['place_of_worship'],
    'academia': ['fitness_centre'],
    'academias': ['fitness_centre'],
    'pet shop': ['pet'],
    'pet shops': ['pet'],
    'floricultura': ['florist'],
    'floriculturas': ['florist'],
	'empresario': ['office=company', 'office=entrepreneur', 'shop=*', 'craft=*', 'industrial=*'],
    'empreendedor': ['office=entrepreneur'],
    'dono de loja': ['shop=*'],
    'industrial': ['industrial=*'],
    'artesao': ['craft=*'],
	'empresarios': ['office=company', 'office=entrepreneur', 'shop=*', 'craft=*', 'industrial=*'],
    'empreendedores': ['office=entrepreneur'],
    'donos de loja': ['shop=*'],
  };

  const normalizedType = businessType.toLowerCase().trim();
  
  if (typeMap[normalizedType]) {
    return typeMap[normalizedType];
  }
  
  for (const [key, values] of Object.entries(typeMap)) {
    if (normalizedType.includes(key) || key.includes(normalizedType)) {
      return values;
    }
  }
  
  return ['*'];
};

const selectResultsToDisplay = (leads: Lead[], businessType: string, location: string, subscriptionPlan?: string): Lead[] => {
  // 1. Ordena por prioridade
  const sortedLeads = [...leads].sort((a, b) => {
    const scoreA = (a.phone ? 100 : 0) + (a.email ? 50 : 0) + (a.address ? 10 : 0);
    const scoreB = (b.phone ? 100 : 0) + (b.email ? 50 : 0) + (b.address ? 10 : 0);
    return scoreB - scoreA;
  });

  // 2. Gera um seed baseado na data
  const today = new Date();
  const seed = parseInt(`${today.getFullYear()}${today.getMonth() + 1}${today.getDate()}`);
  
  // 3. Embaralha os leads de mesma prioridade
  const shuffledLeads = shuffleSamePriorityGroups(sortedLeads, seed);

  // 4. Rotaciona com base no número de buscas
  const cacheKey = `${businessType}-${location}`;
  const searchIndex = incrementSearchCount(cacheKey);
  const rotatedLeads = rotateArray(shuffledLeads, searchIndex);

  // 5. Define a quantidade de resultados com base no plano
  // CORREÇÃO MELHORADA: Garante que "initial" seja o padrão se o plano for nulo ou indefinido.
  const isInitialPlan = !subscriptionPlan || subscriptionPlan === "initial";
  const resultCount = isInitialPlan ? 3 : 10; // Ajuste o '10' para o número do plano pago
  
  console.log(`🔍 Plano: "${subscriptionPlan}", É inicial: ${isInitialPlan}, Resultados: ${resultCount}`);
  
  return rotatedLeads.slice(0, resultCount);
};

// --- Funções auxiliares --- //
const shuffleSamePriorityGroups = (leads: Lead[], seed: number): Lead[] => {
  // Agrupa por "nível de prioridade" (telefone+email, telefone, etc.)
  const priorityGroups: Record<string, Lead[]> = {};
  
  leads.forEach(lead => {
    const priorityKey = `${lead.phone ? '1' : '0'}${lead.email ? '1' : '0'}`;
    if (!priorityGroups[priorityKey]) priorityGroups[priorityKey] = [];
    priorityGroups[priorityKey].push(lead);
  });

  // Embaralha cada grupo (mantendo a ordem entre grupos)
  const shuffled: Lead[] = [];
  Object.keys(priorityGroups).sort().reverse().forEach(key => {
    const group = priorityGroups[key];
    shuffled.push(...shuffleArray(group, seed + parseInt(key))); // Seed único por grupo
  });

  return shuffled;
};

const incrementSearchCount = (cacheKey: string): number => {
  const count = (searchHistory[cacheKey] || 0) + 1;
  searchHistory[cacheKey] = count;
  return count;
};

export const sortLeadsByPriority = (leads: Lead[]): Lead[] => {
  return leads.sort((a, b) => {
    // Função para calcular a pontuação com pesos diferentes
    const getScore = (lead: Lead) => {
      return (lead.phone ? 100 : 0) +  // Peso maior para telefone
             (lead.email ? 50 : 0) +   // Peso médio para email
             (lead.address ? 10 : 0);  // Peso menor para endereço
    };

    const aScore = getScore(a);
    const bScore = getScore(b);

    // Se a pontuação for igual, randomiza para variar os resultados
    if (aScore === bScore) {
      return Math.random() > 0.5 ? 1 : -1;
    }
    
    return bScore - aScore; // Ordena do maior para o menor
  });
};

const detectEstado = (searchTerm: string): string | undefined => {
  // Primeiro verifica padrões com hífen em qualquer parte (Fortaleza-CE, São Paulo-SP)
  const hyphenMatch = searchTerm.match(/-([A-Z]{2})\b/);
  if (hyphenMatch) {
    return hyphenMatch[1];
  }

  // Depois verifica padrões com vírgula (Ituiutaba, MG)
  const commaMatch = searchTerm.match(/,\s*([A-Z]{2})$/);
  if (commaMatch) {
    return commaMatch[1];
  }

  // Verifica siglas isoladas
  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  const upperTerm = searchTerm.toUpperCase();
  
  for (const estado of estados) {
    const regex = new RegExp(`\\b${estado}\\b`);
    if (regex.test(upperTerm)) {
      return estado;
    }
  }

  // Verifica nomes completos de estados
  const estadosCompletos: { [key: string]: string } = {
    'SÃO PAULO': 'SP', 'RIO DE JANEIRO': 'RJ', 'MINAS GERAIS': 'MG',
    'BAHIA': 'BA', 'PARANÁ': 'PR', 'RIO GRANDE DO SUL': 'RS',
    'PERNAMBUCO': 'PE', 'CEARÁ': 'CE', 'SANTA CATARINA': 'SC',
    'GOIÁS': 'GO', 'MARANHÃO': 'MA', 'PARAÍBA': 'PB',
    'PARÁ': 'PA', 'ESPÍRITO SANTO': 'ES', 'PIAUÍ': 'PI',
    'ALAGOAS': 'AL', 'RIO GRANDE DO NORTE': 'RN', 'MATO GROSSO': 'MT',
    'MATO GROSSO DO SUL': 'MS', 'DISTRITO FEDERAL': 'DF',
    'SERGIPE': 'SE', 'RONDÔNIA': 'RO', 'ACRE': 'AC',
    'AMAZONAS': 'AM', 'RORAIMA': 'RR', 'AMAPÁ': 'AP', 'TOCANTINS': 'TO'
  };

  for (const [nomeCompleto, sigla] of Object.entries(estadosCompletos)) {
    if (upperTerm.includes(nomeCompleto)) {
      return sigla;
    }
  }

  return undefined;
};

const getRegionCoords = (region: string): { lat: number, lon: number, radius: number } | null => {
  const regionMap: { [key: string]: { lat: number, lon: number, radius: number } } = {
    'triângulo mineiro': { lat: -18.9186, lon: -48.2772, radius: 100000 },
    'vale do aço': { lat: -19.4700, lon: -42.5478, radius: 80000 },
    'zona da mata': { lat: -21.7640, lon: -43.3494, radius: 120000 },
    'grande são paulo': { lat: -23.5505, lon: -46.6333, radius: 50000 },
    'grande belo horizonte': { lat: -19.9167, lon: -43.9345, radius: 60000 },
  };

  const normalizedRegion = region.toLowerCase().trim();
  return regionMap[normalizedRegion] || null;
};

const getEstadoCoords = (estado: string): { lat: number, lon: number, radius: number } | null => {
  const coordsMap: { [key: string]: { lat: number, lon: number, radius: number } } = {
    'SP': { lat: -23.55, lon: -46.63, radius: 200000 },
    'RJ': { lat: -22.91, lon: -43.20, radius: 100000 },
    'MG': { lat: -19.92, lon: -43.94, radius: 300000 },
    'BA': { lat: -12.97, lon: -38.51, radius: 300000 },
    'PR': { lat: -25.42, lon: -49.27, radius: 200000 },
    'RS': { lat: -30.03, lon: -51.23, radius: 300000 },
    'PE': { lat: -8.05, lon: -34.90, radius: 150000 },
    'CE': { lat: -3.73, lon: -38.52, radius: 200000 },
    'SC': { lat: -27.59, lon: -48.55, radius: 150000 },
    'GO': { lat: -16.68, lon: -49.26, radius: 200000 },
    'MA': { lat: -2.53, lon: -44.30, radius: 200000 },
    'PB': { lat: -7.12, lon: -34.88, radius: 100000 },
    'PA': { lat: -1.46, lon: -48.50, radius: 200000 },
    'ES': { lat: -20.32, lon: -40.34, radius: 100000 },
    'PI': { lat: -5.09, lon: -42.80, radius: 150000 },
    'AL': { lat: -9.67, lon: -35.73, radius: 80000 },
    'RN': { lat: -5.79, lon: -35.21, radius: 100000 },
    'MT': { lat: -15.60, lon: -56.10, radius: 300000 },
    'MS': { lat: -20.46, lon: -54.61, radius: 200000 },
    'DF': { lat: -15.78, lon: -47.93, radius: 50000 },
    'SE': { lat: -10.95, lon: -37.07, radius: 80000 },
    'RO': { lat: -8.76, lon: -63.90, radius: 150000 },
    'AC': { lat: -9.97, lon: -67.81, radius: 100000 },
    'AM': { lat: -3.10, lon: -60.02, radius: 300000 },
    'RR': { lat: 2.82, lon: -60.67, radius: 100000 },
    'AP': { lat: 0.03, lon: -51.07, radius: 80000 },
    'TO': { lat: -10.18, lon: -48.33, radius: 150000 },
    'BR': { lat: -14.24, lon: -51.93, radius: 600000 }
  };

  return coordsMap[estado] || null;
};

const getCityCoords = (city: string): { lat: number, lon: number, radius: number } | null => {
  const cityMap: { [key: string]: { lat: number, lon: number, radius: number } } = {
    'são paulo': { lat: -23.5505, lon: -46.6333, radius: 50000 },
    'rio de janeiro': { lat: -22.9068, lon: -43.1729, radius: 50000 },
    'belo horizonte': { lat: -19.9167, lon: -43.9345, radius: 40000 },
    'brasília': { lat: -15.7942, lon: -47.8822, radius: 40000 },
    'fortaleza': { lat: -3.7275, lon: -38.5275, radius: 40000 },
    'salvador': { lat: -12.9711, lon: -38.5108, radius: 40000 },
    'curitiba': { lat: -25.4296, lon: -49.2719, radius: 40000 },
    'recife': { lat: -8.0500, lon: -34.9000, radius: 30000 },
    'porto alegre': { lat: -30.0331, lon: -51.2300, radius: 40000 },
    'manaus': { lat: -3.1019, lon: -60.0250, radius: 50000 },
    'belém': { lat: -1.4558, lon: -48.4903, radius: 30000 },
    'goiânia': { lat: -16.6806, lon: -49.2564, radius: 30000 },
    'guarulhos': { lat: -23.4539, lon: -46.5339, radius: 30000 },
    'campinas': { lat: -22.9056, lon: -47.0608, radius: 30000 },
    'são luís': { lat: -2.5300, lon: -44.3000, radius: 30000 },
    'maceió': { lat: -9.6658, lon: -35.7353, radius: 20000 },
    'joão pessoa': { lat: -7.1200, lon: -34.8800, radius: 20000 },
    'natal': { lat: -5.7950, lon: -35.2094, radius: 20000 },
    'teresina': { lat: -5.0950, lon: -42.8092, radius: 20000 },
    'aracaju': { lat: -10.9472, lon: -37.0731, radius: 20000 },
    'florianópolis': { lat: -27.5935, lon: -48.5585, radius: 20000 },
    'cuiabá': { lat: -15.5961, lon: -56.0967, radius: 30000 },
    'campo grande': { lat: -20.4697, lon: -54.6200, radius: 30000 },
    'porto velho': { lat: -8.7619, lon: -63.9039, radius: 30000 },
    'rio branco': { lat: -9.9756, lon: -67.8097, radius: 20000 },
    'macapá': { lat: 0.0339, lon: -51.0653, radius: 20000 },
    'vitória': { lat: -20.3194, lon: -40.3378, radius: 20000 },
    'palmas': { lat: -10.1844, lon: -48.3336, radius: 20000 },
    'boa vista': { lat: 2.8194, lon: -60.6717, radius: 20000 },
    'são bernardo do campo': { lat: -23.6939, lon: -46.5650, radius: 30000 },
    'osasco': { lat: -23.5329, lon: -46.7919, radius: 20000 },
    'santo andré': { lat: -23.6572, lon: -46.5332, radius: 20000 },
    'são josé dos campos': { lat: -23.1791, lon: -45.8872, radius: 30000 },
    'ribeirão preto': { lat: -21.1767, lon: -47.8208, radius: 30000 },
    'uberlândia': { lat: -18.9186, lon: -48.2772, radius: 30000 },
    'sorocaba': { lat: -23.5017, lon: -47.4581, radius: 30000 },
    'contagem': { lat: -19.9317, lon: -44.0539, radius: 20000 },
    'juiz de fora': { lat: -21.7640, lon: -43.3494, radius: 20000 },
    'araraquara': { lat: -21.7939, lon: -48.1758, radius: 20000 },
    'santos': { lat: -23.9608, lon: -46.3336, radius: 20000 },
    'niterói': { lat: -22.8833, lon: -43.1036, radius: 20000 },
    'caxias do sul': { lat: -29.1678, lon: -51.1789, radius: 20000 },
    'pelotas': { lat: -31.7719, lon: -52.3428, radius: 20000 },
    'joinville': { lat: -26.3039, lon: -48.8458, radius: 20000 },
    'londrina': { lat: -23.3100, lon: -51.1628, radius: 20000 },
    'maringá': { lat: -23.4250, lon: -51.9389, radius: 20000 },
    'ponta grossa': { lat: -25.0950, lon: -50.1619, radius: 20000 },
    'feira de santana': { lat: -12.2669, lon: -38.9669, radius: 20000 },
    'ilhéus': { lat: -14.7889, lon: -39.0500, radius: 20000 },
    'itabuna': { lat: -14.7856, lon: -39.2803, radius: 20000 },
    'jundiaí': { lat: -23.1864, lon: -46.8844, radius: 20000 },
    'bauru': { lat: -22.3147, lon: -49.0606, radius: 20000 },
    'piracicaba': { lat: -22.7253, lon: -47.6492, radius: 20000 },
    'uberaba': { lat: -19.7478, lon: -47.9319, radius: 20000 },
    'vila velha': { lat: -20.3297, lon: -40.2925, radius: 20000 },
    'serra': { lat: -20.1289, lon: -40.3078, radius: 20000 },
    'cariacica': { lat: -20.2639, lon: -40.4200, radius: 20000 },
    'viamão': { lat: -30.0808, lon: -51.0228, radius: 20000 },
    'são leopoldo': { lat: -29.7600, lon: -51.1469, radius: 20000 },
    'canoas': { lat: -29.9178, lon: -51.1836, radius: 20000 },
    'petrópolis': { lat: -22.5050, lon: -43.1789, radius: 20000 },
    'volta redonda': { lat: -22.5231, lon: -44.1042, radius: 20000 },
    'novo hamburgo': { lat: -29.6783, lon: -51.1306, radius: 20000 },
    'camaçari': { lat: -12.6978, lon: -38.3242, radius: 20000 },
    'itajaí': { lat: -26.9075, lon: -48.6619, radius: 20000 },
    'blumenau': { lat: -26.9189, lon: -49.0658, radius: 20000 },
    'são josé': { lat: -27.6136, lon: -48.6367, radius: 20000 },
    'divinópolis': { lat: -20.1439, lon: -44.8900, radius: 20000 },
    'sete lagoas': { lat: -19.4658, lon: -44.2469, radius: 20000 },
    'ipatinga': { lat: -19.4700, lon: -42.5478, radius: 20000 },
    'juazeiro do norte': { lat: -7.2131, lon: -39.3153, radius: 20000 },
    'maracanaú': { lat: -3.8769, lon: -38.6258, radius: 20000 },
    'são gonçalo': { lat: -22.8269, lon: -43.0539, radius: 20000 },
    'duque de caxias': { lat: -22.7858, lon: -43.3117, radius: 20000 },
    'são josé do rio preto': { lat: -20.8200, lon: -49.3797, radius: 20000 },
    'jacareí': { lat: -23.3053, lon: -45.9658, radius: 20000 },
    'americana': { lat: -22.7378, lon: -47.3336, radius: 20000 },
    'araras': { lat: -22.3569, lon: -47.3839, radius: 20000 },
    'barueri': { lat: -23.5111, lon: -46.8761, radius: 20000 },
    'franca': { lat: -20.5389, lon: -47.4008, radius: 20000 },
    'presidente prudente': { lat: -22.1256, lon: -51.3889, radius: 20000 },
    'são carlos': { lat: -22.0178, lon: -47.8908, radius: 20000 },
    'taubaté': { lat: -23.0344, lon: -45.5553, radius: 20000 },
    'ituiutaba': { lat: -18.9747, lon: -49.4622, radius: 15000 },
    'uberlandia': { lat: -18.9186, lon: -48.2772, radius: 30000 },
    'montes claros': { lat: -16.7289, lon: -43.8639, radius: 30000 },
    'pouso alegre': { lat: -22.2286, lon: -45.9386, radius: 20000 },
    'passos': { lat: -20.7219, lon: -46.6108, radius: 20000 },
    'patos de minas': { lat: -18.5789, lon: -46.5181, radius: 20000 },
    'varginha': { lat: -21.5558, lon: -45.4369, radius: 20000 },
    'santa maria': { lat: -29.6842, lon: -53.8069, radius: 20000 },
    'vitória da conquista': { lat: -14.8657, lon: -40.8389, radius: 20000 },
    'juazeiro': { lat: -9.4117, lon: -40.5028, radius: 20000 },
    'sobral': { lat: -3.6853, lon: -40.3492, radius: 20000 },
    'cascavel': { lat: -24.9558, lon: -53.4558, radius: 20000 },
    'petrolina': { lat: -9.3889, lon: -40.5000, radius: 20000 },
    'caruaru': { lat: -8.2842, lon: -35.9700, radius: 20000 },
    'paulista': { lat: -7.9408, lon: -34.8728, radius: 20000 },
    'chapecó': { lat: -27.1000, lon: -52.6169, radius: 20000 },
    'rio verde': { lat: -17.7978, lon: -50.9278, radius: 20000 },
    'aparecida de goiânia': { lat: -16.8222, lon: -49.2439, radius: 20000 },
    'anápolis': { lat: -16.3269, lon: -48.9528, radius: 20000 }
  };

  const normalizedCity = city.toLowerCase().trim();
  return cityMap[normalizedCity] || null;
};

const getBrasilRegionalCoords = (): { lat: number, lon: number, radius: number }[] => {
  return [
    { lat: -23.55, lon: -46.63, radius: 300000 },
    { lat: -8.05, lon: -34.90, radius: 300000 },
    { lat: -30.03, lon: -51.23, radius: 300000 },
    { lat: -15.60, lon: -56.10, radius: 300000 },
    { lat: -3.10, lon: -60.02, radius: 300000 },
    { lat: -12.97, lon: -38.51, radius: 300000 },
    { lat: -19.92, lon: -43.94, radius: 300000 },
    { lat: -25.42, lon: -49.27, radius: 300000 },
  ];
};

const extractEmailFromWebsite = (website: string): string | null => {
  if (website.includes('facebook.com')) {
    const nameMatch = website.match(/pages\/([^\/]+)/);
    if (nameMatch) {
      const name = nameMatch[1].replace(/%C3%A1/g, 'a').replace(/%C3%A9/g, 'e').replace(/%C3%AD/g, 'i');
      return `contato@${name.toLowerCase().replace(/[^a-z0-9]/g, '')}.com.br`;
    }
  }
  
  try {
    const url = new URL(website);
    const domain = url.hostname.replace('www.', '');
    return `contato@${domain}`;
  } catch {
    return null;
  }
};

const generateEmail = (name?: string): string => {
  //const normalized = name 
    //? name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        //.replace(/\s+/g, ".").replace(/[^a-z0-9.]/g, "")
    //: "contato";
  //return `${normalized}@exemplo.com`;
  return ""; //Linha acrescentada para não gerar emails de exemplo
};

const formatAddress = (element: any, cidade: string, estado?: string): string => {
  const tags = element.tags || {};
  const addressParts = [];
  
  if (tags['addr:street']) {
    addressParts.push(tags['addr:street']);
  }
  if (tags['addr:housenumber']) {
    addressParts.push(tags['addr:housenumber']);
  }
  if (tags['addr:suburb']) {
    addressParts.push(tags['addr:suburb']);
  }
  
  if (addressParts.length === 0) {
    return `${cidade}${estado ? ` - ${estado}` : ''}`;
  }
  
  const baseAddress = addressParts.join(', ');
  return `${baseAddress}, ${cidade}${estado ? ` - ${estado}` : ''}`;
};

// Modifique a função buildMainQuery
const buildMainQuery = (businessType: string, location: string, amenityTags: string[], estado?: string): { query: string; name: string } => {
  const locationParts = location.split('-');
  let finalLocation = location;
  let finalEstado = estado;
  
  if (locationParts.length > 1 && locationParts[1].match(/^[A-Z]{2}$/)) {
    finalLocation = locationParts[0].trim();
    finalEstado = locationParts[1].trim();
  }

  const isHeavy = isHeavySearch(finalLocation, businessType, finalEstado);

  if (finalEstado) {
    return buildHierarchicalQuery(businessType, finalLocation, finalEstado, amenityTags, isHeavy);
  }

  if (isHeavy) {
    return buildHeavyQuery(businessType, finalLocation, amenityTags);
  }

  // Busca normal
  if (amenityTags && amenityTags.length > 0 && !amenityTags.includes('*')) {
    let query = `[out:json][timeout:300];
(
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"];
      way(area.search_area)[${key}="${value}"];
      relation(area.search_area)[${key}="${value}"];`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop];
      way(area.search_area)[shop];
      relation(area.search_area)[shop];`;
    } else {
      return `node(area.search_area)[amenity="${tag}"];
      way(area.search_area)[amenity="${tag}"];
      relation(area.search_area)[amenity="${tag}"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;
    
    return { query, name: `Busca específica: ${businessType} em ${finalLocation}` };
  }
  
  // Busca ampla
  let query = `[out:json][timeout:300];
(
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  node(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  way(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  relation(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
);
out body center qt 1000;`;

  return { query, name: `Busca ampla em ${finalLocation}` };
};

// Nova função para buscas pesadas
const buildHeavyQuery = (businessType: string, location: string, amenityTags: string[]): { query: string; name: string } => {
  console.log(`⚡ Busca pesada detectada para ${location}, aplicando filtro phone/email`);
  
  let query = `[out:json][timeout:300];
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"]["phone"]["email"];
      way(area.search_area)[${key}="${value}"]["phone"]["email"];
      relation(area.search_area)[${key}="${value}"]["phone"]["email"];`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop]["phone"]["email"];
      way(area.search_area)[shop]["phone"]["email"];
      relation(area.search_area)[shop]["phone"]["email"];`;
    } else {
      return `node(area.search_area)[amenity="${tag}"]["phone"]["email"];
      way(area.search_area)[amenity="${tag}"]["phone"]["email"];
      relation(area.search_area)[amenity="${tag}"]["phone"]["email"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return { 
    query, 
    name: `Busca com filtro (área grande): ${businessType} em ${location}` 
  };
};

// Modifique a função buildHierarchicalQuery
const buildHierarchicalQuery = (
  businessType: string, 
  location: string, 
  estado: string, 
  amenityTags: string[],
  isHeavy: boolean = false
): { query: string; name: string } => {
  
  const estadoNames: Record<string, string> = {
    'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas', 'BA': 'Bahia', 
    'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo', 'GO': 'Goiás', 
    'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul', 'MG': 'Minas Gerais', 
    'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná', 'PE': 'Pernambuco', 'PI': 'Piauí', 
    'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte', 'RS': 'Rio Grande do Sul', 
    'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina', 'SP': 'São Paulo', 
    'SE': 'Sergipe', 'TO': 'Tocantins'
  };

  const estadoName = estadoNames[estado] || estado;
  const isStateSearch = location.toLowerCase() === estadoName.toLowerCase() || 
                       location.toLowerCase() === estado.toLowerCase() ||
                       location.toLowerCase() === 'brasil';

  // APLICA FILTRO SE FOR BUSCA PESADA
  const phoneEmailFilter = isHeavy ? '["phone"]["email"]' : '';

  if (isStateSearch) {
    const query = `[out:json][timeout:300];
rel["name"="${estadoName}"]["type"="boundary"]["admin_level"="4"];
map_to_area -> .search_area;

(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"]${phoneEmailFilter};
      way(area.search_area)[${key}="${value}"]${phoneEmailFilter};
      relation(area.search_area)[${key}="${value}"]${phoneEmailFilter};`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop]${phoneEmailFilter};
      way(area.search_area)[shop]${phoneEmailFilter};
      relation(area.search_area)[shop]${phoneEmailFilter};`;
    } else {
      return `node(area.search_area)[amenity="${tag}"]${phoneEmailFilter};
      way(area.search_area)[amenity="${tag}"]${phoneEmailFilter};
      relation(area.search_area)[amenity="${tag}"]${phoneEmailFilter};`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

    return { query, name: `Busca estadual: ${businessType} em ${estadoName}` };
  }

  // Busca em cidade dentro do estado (mantém lógica original)
  const query = `[out:json][timeout:300];
rel["name"="${estadoName}"]["type"="boundary"]["admin_level"="4"];
map_to_area -> .state_area;

(
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="7"];
);
map_to_area -> .search_area;

(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"]${phoneEmailFilter};
      way(area.search_area)[${key}="${value}"]${phoneEmailFilter};
      relation(area.search_area)[${key}="${value}"]${phoneEmailFilter};`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop]${phoneEmailFilter};
      way(area.search_area)[shop]${phoneEmailFilter};
      relation(area.search_area)[shop]${phoneEmailFilter};`;
    } else {
      return `node(area.search_area)[amenity="${tag}"]${phoneEmailFilter};
      way(area.search_area)[amenity="${tag}"]${phoneEmailFilter};
      relation(area.search_area)[amenity="${tag}"]${phoneEmailFilter};`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca hierárquica: ${businessType} em ${location} (${estado})`
  };
};

// Adicione esta função para detectar buscas gerais
const isGeneralLocationSearch = (location: string): boolean => {
  const generalTerms = [
    'brasil', 'brazil', 'minas gerais', 'mg', 'são paulo', 'sp', 
    'rio de janeiro', 'rj', 'bahia', 'ba', 'paraná', 'pr', 'rio grande do sul', 'rs',
    'pernambuco', 'pe', 'ceará', 'ce', 'santa catarina', 'sc', 'goiás', 'go',
    'acre', 'ac', 'alagoas', 'al', 'amapá', 'ap', 'amazonas', 'am', 
	'distrito federal', 'df', 'espírito santo', 'es', 'maranhão', 'ma',
	'mato grosso', 'mt', 'mato grosso do sul', 'ms', 'pará', 'pa', 'paraíba', 'pb', 
	'piauí', 'pi', 'rio grande do norte', 'rn', 'rondônia', 'ro',
    'roraima', 'rr', 'sergipe', 'se', 'tocantins', 'to',
  ];
  
  return generalTerms.includes(location.toLowerCase().trim());
};

// Modifique a função isStateSearch para ser mais abrangente
const isStateSearch = (location: string, businessType: string): boolean => {
  const estados = [
    'acre', 'alagoas', 'amapá', 'amazonas', 'bahia', 'ceará', 'distrito federal', 
    'espírito santo', 'goiás', 'maranhão', 'mato grosso', 'mato grosso do sul', 
    'minas gerais', 'pará', 'paraíba', 'paraná', 'pernambuco', 'piauí', 
    'rio de janeiro', 'rio grande do norte', 'rio grande do sul', 'rondônia', 
    'roraima', 'santa catarina', 'são paulo', 'sergipe', 'tocantins', 'brasil'
  ];
  
  const normalizedLocation = location.toLowerCase().trim();
  return estados.includes(normalizedLocation);
};

// A função buildOverpassQuery deve ficar assim:
const buildOverpassQuery = (businessType: string, location: string, amenityTags: string[], estado?: string): { query: string; name: string }[] => {
  const queries = [];
  
  // ====== BUSCA PRINCIPAL específica ======
  const mainQuery = buildMainQuery(businessType, location, amenityTags, estado);
  queries.push(mainQuery);
  
  // ====== NOVA: BUSCA GERAL na mesma cidade (antes do fallback Brasil) ======
  const generalQuery = buildGeneralQuery(location, estado);
  queries.push(generalQuery);
  
  // ====== BUSCA DE FALLBACK no Brasil se não encontrar ======
  const brazilQuery = buildBrazilQuery(businessType, amenityTags);
  queries.push(brazilQuery);
  
  return queries;
};

// 2. NOVA FUNÇÃO: Busca geral de estabelecimentos na cidade
const buildGeneralQuery = (location: string, estado?: string): { query: string; name: string } => {
  // Se tem estado definido, usa abordagem hierárquica
  if (estado) {
    const estadoNames: Record<string, string> = {
      'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas', 'BA': 'Bahia', 
      'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo', 'GO': 'Goiás', 
      'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul', 'MG': 'Minas Gerais', 
      'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná', 'PE': 'Pernambuco', 'PI': 'Piauí', 
      'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte', 'RS': 'Rio Grande do Sul', 
      'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina', 'SP': 'São Paulo', 
      'SE': 'Sergipe', 'TO': 'Tocantins'
    };

    const estadoName = estadoNames[estado] || estado;

    const query = `[out:json][timeout:300];
// 1. Encontra o estado
rel["name"="${estadoName}"]["type"="boundary"]["admin_level"="4"];
map_to_area -> .state_area;

// 2. Encontra a cidade DENTRO do estado
(
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="7"];
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="4"];
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;

// 3. Busca TODOS os estabelecimentos comerciais
(
  node(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  way(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  relation(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
);
out body center qt 1000;`;

    return {
      query,
      name: `Busca geral de estabelecimentos em ${location} (${estado})`
    };
  }

  // Busca tradicional sem estado
  const query = `[out:json][timeout:300];
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  node(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  way(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  relation(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca geral de estabelecimentos em ${location}`
  };
};

const filterFallbackByBusinessType = (contacts: FallbackContact[], businessType: string): FallbackContact[] => {
  const normalizedType = businessType.toLowerCase().trim();
  const MIN_RESULTS = 3;
  
  console.log(`🔍 Buscando fallback para: "${businessType}" (normalizado: "${normalizedType}")`);
  
  // CASO ESPECIAL: "estabelecimento" ou "estabelecimentos" retorna TODOS os contatos
  if (normalizedType === 'estabelecimento' || normalizedType === 'estabelecimentos' || normalizedType === 'negócio' || normalizedType === 'negócios' || normalizedType === 'serviço' || normalizedType === 'serviços') {
    console.log('🏢 Busca por estabelecimentos gerais - retornando todos os tipos');
    return shuffleArray([...contacts], Date.now() % 1000).slice(0, 5);
  }
  
  // Se o tipo de negócio estiver vazio, retorna contatos gerais
  if (!normalizedType) {
    console.log('🔄 Tipo de negócio vazio, retornando contatos gerais');
    return shuffleArray([...contacts], Date.now() % 1000).slice(0, MIN_RESULTS);
  }

  // Mapear tipos de negócio para palavras-chave no fallback - EXPANDIDO
  const businessKeywords: Record<string, string[]> = {
    // Alimentação - EXPANDIDO
    'restaurante': ['restaurante', 'comida', 'alimentação', 'gastro', 'culinária', 'lanche', 'pizza', 'bar', 'degust', 'doce', 'fest'],
    'restaurantes': ['restaurante', 'comida', 'alimentação', 'gastro', 'culinária', 'lanche', 'pizza', 'bar', 'degust', 'doce', 'fest'],
    'lanchonete': ['lanchonete', 'lanche', 'fast', 'comida', 'alimentação', 'restaurante', 'doce'],
    'padaria': ['padaria', 'pão', 'doce', 'confeitaria', 'alimentação'],
    'bar': ['bar', 'pub', 'bebida', 'drink', 'cerveja'],
    'pizzaria': ['pizza', 'restaurante', 'comida', 'italiana'],
    
    // Saúde
    'farmacia': ['farmacia', 'medicamento', 'saude', 'hospital', 'clinica', 'drogaria'],
    'hospital': ['hospital', 'saude', 'medicina', 'clinica', 'municipal', 'doutor', 'dr'],
    'clinica': ['clinica', 'saude', 'medicina', 'hospital', 'doutor', 'dr'],
    'dentista': ['dentista', 'odonto', 'dental', 'dente'],
    
    // Comércio
    'supermercado': ['supermercado', 'mercado', 'super', 'alimentação', 'compras', 'atacado'],
    'loja': ['loja', 'shop', 'comercio', 'venda', 'compras'],
    
    // Serviços
    'oficina': ['oficina', 'mecanica', 'auto', 'carro', 'veiculo', 'peças', 'motor'],
    'salao': ['salao', 'beleza', 'cabelo', 'estetica', 'barbearia', 'hair'],
    'academia': ['academia', 'fitness', 'ginastica', 'exercicio', 'esporte'],
    
    // Hospedagem
    'hotel': ['hotel', 'hospedagem', 'pousada', 'turismo'],
    'pousada': ['pousada', 'hotel', 'hospedagem', 'turismo']
  };

  const keywords = businessKeywords[normalizedType] || [normalizedType];
  console.log(`🔍 Palavras-chave para busca: ${keywords.join(', ')}`);
  
  // Filtrar contatos que contenham as palavras-chave no nome
  let filtered = contacts.filter(contact => {
    const contactName = contact.name.toLowerCase();
    const matches = keywords.some(keyword => contactName.includes(keyword.toLowerCase()));
    if (matches) {
      console.log(`✅ Encontrado: ${contact.name} (contém: ${keywords.find(k => contactName.includes(k.toLowerCase()))})`);
    }
    return matches;
  });

  console.log(`🔍 Filtro específico encontrou: ${filtered.length} resultados`);

  // Se não encontrou nenhum, tenta uma busca mais ampla
  if (filtered.length === 0) {
    console.log(`⚠️ Nenhum fallback específico para "${businessType}", fazendo busca ampla`);
    
    // Busca ampla por palavras individuais (se tiver mais de 3 caracteres)
    const searchWords = normalizedType.split(' ').filter(word => word.length > 3);
    console.log(`🔍 Buscando pelas palavras: ${searchWords.join(', ')}`);
    
    filtered = contacts.filter(contact => {
      const contactName = contact.name.toLowerCase();
      return searchWords.some(word => contactName.includes(word));
    });
    
    console.log(`🔍 Busca ampla encontrou: ${filtered.length} resultados`);
  }

  // Se ainda não encontrou resultados, retorna contatos gerais
  if (filtered.length === 0) {
    console.log(`🏢 Nenhuma correspondência específica, retornando estabelecimentos gerais`);
    filtered = [...contacts];
  }

  // Ordena por prioridade (telefone + email primeiro)
  filtered.sort((a, b) => {
    const aScore = (a.phone ? 100 : 0) + (a.email ? 50 : 0) + (a.address ? 10 : 0);
    const bScore = (b.phone ? 100 : 0) + (b.email ? 50 : 0) + (b.address ? 10 : 0);
    return bScore - aScore;
  });

  // Usa o tipo de negócio como seed para o shuffle
  const seed = businessType.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  
  const results = shuffleArray(filtered, seed).slice(0, MIN_RESULTS);
  console.log(`📋 Retornando ${results.length} contatos:`, results.map(r => r.name));
  
  return results;
};

const fetchWithTimeout = async (url: string, options: RequestInit, timeout = 15000): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

const getNextAvailableServer = (servers: string[]): string[] => {
  const now = Date.now();
  
  const availableServers = servers.filter(server => {
    const lastUsed = lastServerUsage.get(server) || 0;
    return now - lastUsed >= SERVER_COOLDOWN_TIME;
  });
  
  if (availableServers.length > 0) {
    return availableServers.sort((a, b) => {
      const usageA = serverUsageMap.get(a) || 0;
      const usageB = serverUsageMap.get(b) || 0;
      return usageA - usageB;
    });
  }
  
  return servers.sort((a, b) => {
    const usageA = serverUsageMap.get(a) || 0;
    const usageB = serverUsageMap.get(b) || 0;
    return usageA - usageB;
  });
};

const markServerUsed = (server: string) => {
  const currentUsage = serverUsageMap.get(server) || 0;
  serverUsageMap.set(server, currentUsage + 1);
  lastServerUsage.set(server, Date.now());
};

// ====== IMPLEMENTAR A FUNÇÃO fetchOverpass que estava faltando ======

const fetchOverpass = async (query: string): Promise<any> => {
  const overpassUrl = 'https://overpass-api.de/api/interpreter';
  
  try {
    const response = await fetch(overpassUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: `data=${encodeURIComponent(query)}`
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('Erro no fetchOverpass:', error);
    throw error;
  }
};

// Atualize a função isHeavySearch para ser mais abrangente
const isHeavySearch = (location: string, businessType: string, estado?: string): boolean => {
  const bigCities = [
    'são paulo', 'rio de janeiro', 'belo horizonte', 'brasília', 'fortaleza',
    'salvador', 'curitiba', 'recife', 'porto alegre', 'manaus', 'belém',
    'goiânia', 'guarulhos', 'campinas', 'são luís', 'maceió', 'joão pessoa',
    'natal', 'teresina', 'aracaju', 'florianópolis', 'cuiabá', 'campo grande',
    'porto velho', 'rio branco', 'macapá', 'vitória', 'palmas', 'boa vista',
    // Adicione outras cidades grandes que podem ter muitas relações de border
    'santo andré', 'são bernardo do campo', 'osasco', 'ribeirão preto', 'uberlândia',
    'sorocaba', 'contagem', 'juiz de fora', 'aracaju', 'joinville', 'londrina',
    'maringá', 'campina grande', 'feira de santana', 'vila velha', 'serra'
  ];

  const estados = [
    'acre', 'alagoas', 'amapá', 'amazonas', 'bahia', 'ceará', 'distrito federal',
    'espírito santo', 'goiás', 'maranhão', 'mato grosso', 'mato grosso do sul',
    'minas gerais', 'pará', 'paraíba', 'paraná', 'pernambuco', 'piauí',
    'rio de janeiro', 'rio grande do norte', 'rio grande do sul', 'rondônia',
    'roraima', 'santa catarina', 'são paulo', 'sergipe', 'tocantins', 'brasil'
  ];

  // ✅ NOVA LÓGICA: Remove o hífen e estado para detectar a cidade base
  let cityToCheck = location.toLowerCase().trim();
  
  // Se tem hífen, pega só a parte antes do hífen (cidade)
  if (cityToCheck.includes('-')) {
    cityToCheck = cityToCheck.split('-')[0].trim();
  }
  
  console.log(`🔍 Verificando se "${cityToCheck}" (original: "${location}") é busca pesada`);
  
  // Se for estado, país, cidade grande → busca pesada
  const isHeavy = estados.includes(cityToCheck) || 
                  bigCities.includes(cityToCheck) ||
                  cityToCheck === 'brasil';
  
  console.log(`${isHeavy ? '⚡' : '🔍'} "${location}" ${isHeavy ? 'É' : 'NÃO É'} busca pesada`);
  
  return isHeavy;
};

// Função para buscar em cidades vizinhas (border) COM FILTRO
const buildNearbyBorderQuery = (businessType: string, location: string, estado?: string): { query: string; name: string } => {
  const isHeavy = isHeavySearch(location, businessType, estado);
  const amenityTags = getAmenityTags(businessType);
  
  // APLICA FILTRO SE FOR BUSCA PESADA
  const phoneEmailFilter = isHeavy ? '["phone"]["email"]' : '';

  const query = `[out:json][timeout:300];
// Encontra a área principal (border da cidade)
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  node["name"="${location}"]["place"="village"];
);
map_to_area -> .main_area;

// Encontra borders vizinhas (outras cidades no raio)
(
  rel(around.main_area:20000)["type"="boundary"]["admin_level"="8"];
);
map_to_area -> .neighbor_areas;

// Busca estabelecimentos NAS ÁREAS VIZINHAS com filtro se necessário
(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.neighbor_areas)[${key}="${value}"]${phoneEmailFilter};
      way(area.neighbor_areas)[${key}="${value}"]${phoneEmailFilter};
      relation(area.neighbor_areas)[${key}="${value}"]${phoneEmailFilter};`;
    } else if (tag === 'shop') {
      return `node(area.neighbor_areas)[shop]${phoneEmailFilter};
      way(area.neighbor_areas)[shop]${phoneEmailFilter};
      relation(area.neighbor_areas)[shop]${phoneEmailFilter};`;
    } else {
      return `node(area.neighbor_areas)[amenity="${tag}"]${phoneEmailFilter};
      way(area.neighbor_areas)[amenity="${tag}"]${phoneEmailFilter};
      relation(area.neighbor_areas)[amenity="${tag}"]${phoneEmailFilter};`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca em borders vizinhas a ${location}${isHeavy ? ' (com filtro)' : ''}`
  };
};

// Função para tentar a busca em borders vizinhas
const tryNearbyBorderSearch = async (businessType: string, location: string, estado?: string): Promise<Lead[]> => {
  const isHeavy = isHeavySearch(location, businessType, estado);
  
  // 1. Primeira tentativa com filtro se for busca pesada
  const query = buildNearbyBorderQuery(businessType, location, estado);
  
  try {
    console.log(`🔍 Primeira tentativa borders: ${query.name}`);
    const response = await fetchOverpass(query.query);
    const results = processOSMElements(response.elements, businessType, location, estado);
    
    // Se é busca pesada e retornou menos de 100 leads, refaz sem filtro
    if (isHeavy && results.length < 100) {
      console.log(`⚡ Busca pesada em borders retornou apenas ${results.length} leads, refazendo sem filtro...`);
      
      // 2. Segunda tentativa sem filtro
      const queryWithoutFilter = buildNearbyBorderQueryWithoutFilter(businessType, location, estado);
      
      try {
        console.log(`🔄 Segunda tentativa borders: ${queryWithoutFilter.name}`);
        const responseWithoutFilter = await fetchOverpass(queryWithoutFilter.query);
        const resultsWithoutFilter = processOSMElements(responseWithoutFilter.elements, businessType, location, estado);
        
        console.log(`✅ Busca borders sem filtro retornou ${resultsWithoutFilter.length} leads`);
        return resultsWithoutFilter;
        
      } catch (errorWithoutFilter) {
        console.error('Erro na busca borders sem filtro:', errorWithoutFilter);
        return results;
      }
    }
    
    return results;
    
  } catch (error) {
    console.error('Erro na busca em borders vizinhas:', error);
    return [];
  }
};

const buildNearbyBorderQueryWithoutFilter = (businessType: string, location: string, estado?: string): { query: string; name: string } => {
  const amenityTags = getAmenityTags(businessType);
  
  // SEM FILTRO de phone/email
  const phoneEmailFilter = '';

  const query = `[out:json][timeout:300];
// Encontra a área principal (border da cidade)
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  node["name"="${location}"]["place"="village"];
);
map_to_area -> .main_area;

// Encontra borders vizinhas (outras cidades no raio)
(
  rel(around.main_area:20000)["type"="boundary"]["admin_level"="8"];
);
map_to_area -> .neighbor_areas;

// Busca estabelecimentos NAS ÁREAS VIZINHAS SEM filtro
(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.neighbor_areas)[${key}="${value}"];
      way(area.neighbor_areas)[${key}="${value}"];
      relation(area.neighbor_areas)[${key}="${value}"];`;
    } else if (tag === 'shop') {
      return `node(area.neighbor_areas)[shop];
      way(area.neighbor_areas)[shop];
      relation(area.neighbor_areas)[shop];`;
    } else {
      return `node(area.neighbor_areas)[amenity="${tag}"];
      way(area.neighbor_areas)[amenity="${tag}"];
      relation(area.neighbor_areas)[amenity="${tag}"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca em borders vizinhas SEM FILTRO a ${location}`
  };
};

// Modifique a função de busca em cidades vizinhas
const buildNearbyCitiesQuery = (businessType: string, location: string, estado?: string): { query: string; name: string } => {
  const isHeavy = isHeavySearch(location, businessType, estado, 'nearby');
  const amenityTags = getAmenityTags(businessType);
  
  // APLICA FILTRO SE FOR BUSCA PESADA
  const phoneEmailFilter = isHeavy ? '["phone"]["email"]' : '';

  const query = `[out:json][timeout:300];
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  node["name"="${location}"]["place"="village"];
);
map_to_area -> .main_area;

(
  rel(around.main_area:20000)["type"="boundary"]["admin_level"="8"];
);
map_to_area -> .neighbor_areas;

(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.neighbor_areas)[${key}="${value}"]${phoneEmailFilter};
      way(area.neighbor_areas)[${key}="${value}"]${phoneEmailFilter};`;
    } else if (tag === 'shop') {
      return `node(area.neighbor_areas)[shop]${phoneEmailFilter};
      way(area.neighbor_areas)[shop]${phoneEmailFilter};`;
    } else {
      return `node(area.neighbor_areas)[amenity="${tag}"]${phoneEmailFilter};
      way(area.neighbor_areas)[amenity="${tag}"]${phoneEmailFilter};`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca em cidades próximas a ${location}${isHeavy ? ' (com filtro)' : ''}`
  };
};

const trySearchStrategies = async (
  queryStrategies: { query: string; name: string }[],
  servers: string[],
  location: string,
  businessType: string,
  estado?: string,
  maxAttempts = 30 
): Promise<Lead[]> => {
  const amenityTags = getAmenityTags(businessType);
  
  // 1. Verifica se é uma vila/distrito pequeno (remova esta verificação ou ajuste a lógica)
  const isSmallVillage = false; // Desativamos a verificação automática de vilas
  
  if (isSmallVillage) {
    console.log(`📍 Identificada vila/distrito pequeno: ${location}`);
    const villageQueries = buildVillageSearchQuery(location, businessType, amenityTags);
    
    for (const strategy of villageQueries) {
      try {
        console.log(`🏡 Tentando busca em vila: ${strategy.name}`);
        const response = await fetchOverpass(strategy.query);
        
        if (response.elements?.length > 0) {
          const leads = response.elements
            .map((element: any) => mapElementToLead(element, businessType, location, estado))
            .filter((lead: Lead) => lead.name && lead.name !== 'Nome não encontrado');
          
          if (leads.length > 0) {
            console.log(`✅ Encontrados ${leads.length} leads na vila`);
            return leads;
          }
        }
      } catch (error) {
        console.error(`Erro na busca na vila (${strategy.name}):`, error);
      }
    }
    
    console.log('🔍 Nenhum resultado encontrado na busca específica para vila');
  }

  // 2. SE NÃO FOR VILA OU NÃO ENCONTROU NA VILA, FAZ BUSCA NORMAL
  console.log('🔍 Iniciando busca normal em Overpass');
  let lastError: Error | null = null;
  let attempts = 0;
  
  for (const [strategyIndex, strategy] of queryStrategies.entries()) {
    console.log(`\n=== ESTRATÉGIA ${strategyIndex + 1}: ${strategy.name} ===`);
    
    const availableServers = getNextAvailableServer(servers);
    
    for (const server of availableServers) {
      if (attempts >= maxAttempts) {
        console.log(`⏰ Limite de ${maxAttempts} tentativas alcançado`);
        return [];
      }
      
      try {
        attempts++;
        console.log(`🔄 Tentativa ${attempts}: ${strategy.name} no servidor ${server}`);
        
        const response = await fetchWithTimeout(server, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `data=${encodeURIComponent(strategy.query)}`
        }, 20000);

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        markServerUsed(server);
        
        if (data.elements?.length > 0) {
          console.log(`🎯 Sucesso! Encontrados ${data.elements.length} resultados com: ${strategy.name}`);
          
          const leads = data.elements
            .map((element: any) => mapElementToLead(element, businessType, location, estado))
            .filter((lead: Lead) => lead.name && lead.name !== 'Nome não encontrado');
          
          return leads;
        } else {
          console.log(`⚠️ Nenhum resultado encontrado para: ${strategy.name}`);
        }
      } catch (error) {
        console.error(`❌ Erro na tentativa ${attempts}:`, error);
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }
    
    console.log(`❌ Estratégia "${strategy.name}" falhou em todos os servidores`);
  }
  
  console.log('❌ Todas as estratégias falharam');
  return [];
};
  
const searchViaOverpass = async (businessType: string, location: string, estado?: string): Promise<Lead[]> => {
  const amenityTags = getAmenityTags(businessType);
  const overpassServers = [ // Mova para cima da primeira utilização
    'https://overpass-api.de/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
    'https://overpass.openstreetmap.ru/api/interpreter',
    'https://gall.openstreetmap.de/api/interpreter',
    'https://lambert.openstreetmap.de/api/interpreter',
    'https://maps.mail.ru/osm/tools/overpass/api/interpreter',
    'https://overpass.osm.ch/api/interpreter',
    'https://overpass.private.coffee/api/interpreter',
    'https://overpass.osm.jp/api/interpreter',
    'https://overpass.openstreetmap.fr/api/interpreter',
    'https://overpass.osm.rambler.ru/cgi/interpreter',
    'https://overpass.nchc.org.tw/api/interpreter',
    'https://overpass.osm.brasil.xyz/api/interpreter',
    'https://overpass.osm.be/api/interpreter',
    'https://overpass.zibi.me/api/interpreter'
  ];

  const queryStrategies = buildOverpassQuery(businessType, location, amenityTags, estado);
  
  console.log(`🔍 Buscando ${businessType} em ${location}${estado ? ` - ${estado}` : ''}`);
  
  // 1. Busca Principal (área exata)
  console.log('1️⃣ Busca principal na área exata');
  let allResults = await tryMainSearch(businessType, location, amenityTags, estado);
  
  // 2. Busca por Nome da Localidade (se não encontrou)
  if (allResults.length === 0) {
    console.log('2️⃣ Busca por nome da localidade');
    allResults = await tryLocationNameSearch(businessType, location, estado);
  }

  // 3. Busca em Cidades Vizinhas (com filtro automático se necessário)
  if (allResults.length === 0) {
    console.log('3️⃣ Busca em cidades vizinhas (com filtro inteligente)');
    allResults = await tryNearbyCitiesSearch(businessType, location, estado);
  }

  // 4. Fallback para Brasil se ainda não encontrou
  if (allResults.length === 0) {
    console.log('4️⃣ Busca ampla no Brasil (sempre com filtro)');
    const brazilQuery = buildBrazilQuery(businessType, amenityTags);
    
    try {
      const response = await fetchOverpass(brazilQuery.query);
      allResults = processOSMElements(response.elements, businessType, 'Brasil', 'BR');
    } catch (error) {
      console.error('Erro na busca ampla no Brasil:', error);
    }
  }
  
  return allResults;
};

const mapElementToLead = (element: any, businessType: string, location: string, estado?: string): Lead => {
  const tags = element.tags || {};
  const center = element.center || element;
  
  return {
    id: element.id?.toString() || Math.random().toString(),
    name: tags.name || tags['name:pt'] || tags.brand || 'Nome não encontrado',
    phone: tags.phone || tags['contact:phone'] || tags.mobile || '',
    email: tags.email || tags['contact:email'] || extractEmailFromWebsite(tags.website || tags['contact:website'] || '') || generateEmail(tags.name),
    address: formatAddress(element, location, estado),
    source: 'OSM',
    rating: tags.rating || '',
    latitude: center.lat || element.lat || 0,
    longitude: center.lon || element.lon || 0,
    website: tags.website || tags['contact:website'] || tags.url || '',
    hasRealPhone: !!(tags.phone || tags['contact:phone'] || tags.mobile),
    hasRealEmail: !!(tags.email || tags['contact:email'])
  };
};

// ====== FUNÇÃO PARA TENTAR SERVIDORES ALTERNATIVOS ======
const fetchOverpassAlternative = async (query: string, server: string): Promise<any> => {
  const response = await fetch(server, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: `data=${encodeURIComponent(query)}`
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

// ====== FUNÇÃO INTELIGENTE que escolhe entre busca específica ou ampla ======
const buildAreaQuery = (businessType: string, location: string, amenityTags: string[]): { query: string; name: string } => {
  // Se tem tags específicas, faz busca focada
  if (amenityTags && amenityTags.length > 0 && !amenityTags.includes('*')) {
    return buildSpecificQuery(businessType, location, amenityTags);
  }
  
  // Se não tem tags específicas, faz busca ampla
  return buildBroadQuery(businessType, location);
};

// ====== BUSCA ESPECÍFICA usando suas tags existentes ======
const buildSpecificQuery = (businessType: string, location: string, amenityTags: string[]): { query: string; name: string } => {
  let query = `[out:json][timeout:300];
// Define a área de busca
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
// Busca ESPECÍFICA usando suas tags do getAmenityTags()
(
  ${amenityTags.map(tag => {
    // Se a tag contém '=' (ex: "craft=auto_repair"), separa chave e valor
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}=${value}];
      way(area.search_area)[${key}=${value}];
      relation(area.search_area)[${key}=${value}];`;
    } 
    // Se é só a chave (ex: "hospital"), assume que é amenity
    else if (tag === 'shop') {
      return `node(area.search_area)[shop];
      way(area.search_area)[shop];
      relation(area.search_area)[shop];`;
    }
    else {
      return `node(area.search_area)[amenity=${tag}];
      way(area.search_area)[amenity=${tag}];
      relation(area.search_area)[amenity=${tag}];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca específica: ${businessType} em ${location}`
  };
};

// ====== BUSCA AMPLA (sua query original que funciona bem) ======
const buildBroadQuery = (businessType: string, location: string): { query: string; name: string } => {
  let query = `[out:json][timeout:300];
// Define a área de busca  
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
// Busca AMPLA - qualquer estabelecimento comercial
(
  node(area.search_area)["^(shop|amenity|office|tourism|leisure|craft|industrial)$"""];
  way(area.search_area)["^(shop|amenity|office|tourism|leisure|craft|industrial)$"""];
  relation(area.search_area)["^(shop|amenity|office|tourism|leisure|craft|industrial)$"""];
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca ampla de estabelecimentos em ${location}`
  };
};

// ====== VERSÃO ALTERNATIVA para busca específica por tipo de negócio ======
const buildSpecificBusinessQuery = (businessType: string, location: string, amenityTags: string[]): { query: string; name: string } => {
  // Se você quiser buscar por tipo específico de negócio, pode usar esta versão
  const specificTags = amenityTags.length > 0 ? amenityTags : ['*'];
  
  let query = `[out:json][timeout:300];
// Define a área de busca
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${location}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
// Busca específica por tipo de negócio
(
  ${specificTags.map(amenity => {
    if (amenity === '*') {
      return `node(area.search_area)["^(shop|amenity|office|tourism|leisure|craft|industrial)$"""];
      way(area.search_area)["^(shop|amenity|office|tourism|leisure|craft|industrial)$"""];
      relation(area.search_area)["^(shop|amenity|office|tourism|leisure|craft|industrial)$"""];`;
    } else if (amenity === 'shop') {
      return `node(area.search_area)[shop];
      way(area.search_area)[shop];
      relation(area.search_area)[shop];`;
    } else {
      return `node(area.search_area)[amenity=${amenity}];
      way(area.search_area)[amenity=${amenity}];
      relation(area.search_area)[amenity=${amenity}];`;
    }
  }).join('\n')}
);
// Saída completa dos resultados
out body center qt 1000;`;

  return {
    query,
    name: `Busca específica para ${businessType} em ${location}`
  };
};

// Função buildBrazilQuery atualizada (sempre com filtro)
const buildBrazilQuery = (businessType: string, amenityTags: string[]): { query: string; name: string } => {
  // SEMPRE filtra por phone e email em buscas no Brasil (é uma busca muito ampla)
  let query = `[out:json][timeout:300];
(
  rel["name"="Brasil"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"]["phone"]["email"];
      way(area.search_area)[${key}="${value}"]["phone"]["email"];`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop]["phone"]["email"];
      way(area.search_area)[shop]["phone"]["email"];`;
    } else {
      return `node(area.search_area)[amenity="${tag}"]["phone"]["email"];
      way(area.search_area)[amenity="${tag}"]["phone"]["email"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;
  
  return { query, name: `Busca específica no Brasil: ${businessType}` };
};

// Adicione esta função para processar elementos com filtro
const processOSMElementsWithFilter = (elements: any[], businessType: string, location: string, estado?: string, filterPhoneEmail: boolean = false): Lead[] => {
  if (!elements || elements.length === 0) return [];
  
  let filteredElements = elements;
  
  if (filterPhoneEmail) {
    // Filtra elementos que tenham phone E email
    filteredElements = elements.filter((element: any) => {
      const tags = element.tags || {};
      return tags.phone && tags.email;
    });
    
    console.log(`📊 Filtrado para ${filteredElements.length} estabelecimentos com phone e email`);
  }
  
  return filteredElements
    .map((element: any) => mapElementToLead(element, businessType, location, estado))
    .filter((lead: Lead) => lead.name && lead.name !== 'Nome não encontrado');
};

// Adicione esta função para garantir que o fallback seja exibido
const ensureFallbackDisplay = (leads: Lead[], businessType: string): Lead[] => {
  if (leads.length === 0) {
    const filteredFallback = filterFallbackByBusinessType([...BRAZIL_FALLBACK_CONTACTS], businessType);
    return sortLeadsByPriority(filteredFallback.map(contact => ({
      ...contact,
      source: "Base Geral",
      hasRealPhone: !!(contact.phone),
      hasRealEmail: !!(contact.email)
    })));
  }
  return leads;
};

// ✅ Adicionar esta função antes da searchBusinessLeads
const generateAlternativeSearches = (businessType: string, currentLocation: string): AlternativeSearch[] => {
  // Lista de cidades grandes para sugerir
  const popularCities = [
    { name: "São Paulo", state: "SP" },
    { name: "Rio de Janeiro", state: "RJ" },
    { name: "Belo Horizonte", state: "MG" },
    { name: "São Luís", state: "MA" },
    { name: "Fortaleza", state: "CE" },
    { name: "Salvador", state: "BA" },
    { name: "Brasília", state: "DF" },
    { name: "Curitiba", state: "PR" },
    { name: "Porto Alegre", state: "RS" },
    { name: "Recife", state: "PE" },
    { name: "Manaus", state: "AM" },
    { name: "Goiânia", state: "GO" },
    { name: "Belém", state: "PA" },
    { name: "Campinas", state: "SP" },
    { name: "Ribeirão Preto", state: "SP" }
  ];

  // Remove a cidade atual da lista de sugestões
  const currentLocationNormalized = currentLocation.toLowerCase().split('-')[0].trim();
  const availableCities = popularCities.filter(city => 
    city.name.toLowerCase() !== currentLocationNormalized
  );

  // Embaralha e pega 3 cidades aleatórias
  const shuffled = shuffleArray([...availableCities], Date.now() % 1000);
  const selectedCities = shuffled.slice(0, 3);

  return selectedCities.map(city => ({
    businessType,
    location: `${city.name}-${city.state}`,
    displayText: `${businessType} em ${city.name}-${city.state}`
  }));
};

export const searchBusinessLeads = async (
  searchTerm: string, 
  cityName?: string, 
  forceRefresh = false,
  userId?: string,
  subscription?: { plan: string, request_count?: number }
): Promise<LeadsResult> => {
  // ✅ DEBUG INICIAL
  console.log('🔍 DEBUG INICIAL:', {
    userId,
    subscription,
    plan: subscription?.plan,
    requestCount: subscription?.request_count
  });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 60000);
  
  // ✅ CRIA CACHE KEY SIMPLES
  const cacheKey = createCacheKey(searchTerm, cityName);
  
  // ✅ VERIFICAÇÃO DE LIMITE DE CONSULTAS
  const showLimitReached = (subscription?.plan === 'essential' || subscription?.plan === 'initial') && 
                         ((subscription?.request_count ?? 0) <= 0 || 
                          (subscription?.next_billing_date && 
                           new Date(new Date(subscription.next_billing_date).getTime() + 24 * 60 * 60 * 1000) < new Date()));
  
  // Lista priorizada de servidores
  const OVERPASS_SERVERS = [
    'https://overpass-api.de/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
    'https://overpass.nchc.org.tw/api/interpreter',
    'https://overpass.osm.ch/api/interpreter',
    'https://overpass.osm.be/api/interpreter',
    'https://overpass.openstreetmap.ru/api/interpreter',
    'https://overpass.private.coffee/api/interpreter',
    'https://overpass.osm.jp/api/interpreter'
  ];

  if (showLimitReached) {
    return {
      success: false,
      leads: [],
      executionTime: 0,
      sources: [],
      error: "Limite de consultas esgotado",
      message: "Você esgotou suas consultas mensais. Atualize seu plano para continuar."
    };
  }

  // ✅ MUDANÇA CRÍTICA: DECREMENTA PRIMEIRO (para cache também)
  if (userId && subscription?.plan && 
      ['essential', 'initial'].includes(subscription.plan) && 
      subscription.request_count && subscription.request_count > 0) {
    
    console.log('💳 Decrementando contador (busca nova ou cache):', {
      userId,
      plan: subscription.plan,
      currentCount: subscription.request_count
    });
    
    const decremented = await decrementRequestCounter(userId, subscription.request_count);
    if (!decremented) {
      console.error('❌ Falha ao decrementar contador');
      return {
        success: false,
        leads: [],
        executionTime: 0,
        sources: [],
        error: "Erro ao processar consulta"
      };
    } else {
      console.log('✅ Contador decrementado com sucesso');
      subscription.request_count = subscription.request_count - 1;
    }
  }

  // ✅ AGORA verifica o cache (após decrementar)
  if (!forceRefresh && cache.has(cacheKey)) {
    const cachedData = cache.get(cacheKey)!;
    
    // Verifica se não expirou
    if (Date.now() - cachedData.timestamp < CACHE_EXPIRATION_TIME) {
      console.log('📋 Retornando resultado do cache (COM contador decrementado)');
      
      const [businessType, location] = extractSearchParams(searchTerm, cityName);
      const searchIndex = incrementSearchCount(cacheKey);
      
      // ✅ Use TODOS os leads do cache e filtre novamente
      const rotatedLeads = rotateArray(cachedData.data.allLeads, searchIndex);
      const prioritizedLeads = sortLeadsByPriority(rotatedLeads);
      const subscriptionPlan = subscription?.plan || "initial";
      const resultsToDisplay = selectResultsToDisplay(
        prioritizedLeads, 
        businessType, 
        location, 
        subscriptionPlan
      );
      
      return {
        success: true,
        leads: resultsToDisplay,
        sources: cachedData.data.sources,
        executionTime: 5, // Tempo mínimo para cache
        message: cachedData.data.message,
        fallbackUsed: cachedData.data.fallbackUsed,
        originalSearch: cachedData.data.originalSearch,
        suggestions: cachedData.data.suggestions,
        fromCache: true // ✅ Marcar que veio do cache
      };
    } else {
      // Remove cache expirado
      cache.delete(cacheKey);
    }
  }

  const startTime = Date.now();
  let allResults: Lead[] = [];
  let fallbackUsed = false;
  let message: string | undefined = undefined;
  let actualSource = "Busca Online";

  try {
    const [businessType, location] = extractSearchParams(searchTerm, cityName);
    if (!location) throw new Error("Localização é obrigatória");

    const estado = detectEstado(searchTerm) || detectEstado(location);
    const amenityTags = getAmenityTags(businessType);

    console.log(`🔍 Buscando: ${businessType} em ${location}${estado ? ` - ${estado}` : ''}`);

    // 1. Busca Principal (área exata)
    console.log('1️⃣ Busca principal na área exata');
    allResults = await tryMainSearch(businessType, location, amenityTags, estado);
    
    // 2. Busca por Nome da Localidade
    if (allResults.length === 0) {
      console.log('2️⃣ Busca por nome da localidade');
      allResults = await tryLocationNameSearch(businessType, location, estado);
    }

    // 3. Busca em BORDERS VIZINHAS (com filtro inteligente)
    if (allResults.length === 0) {
      console.log('3️⃣ Busca em borders vizinhas (filtro automático)');
      allResults = await tryNearbyBorderSearch(businessType, location, estado);
    }

    // 4. Fallback se todas as tentativas falharem
    if (allResults.length === 0) {
      console.log('🔄 Usando fallback inteligente...');
      allResults = getIntelligentFallback(businessType, location);
      fallbackUsed = true;
      actualSource = "Base Geral";
      message = `Nenhum resultado encontrado para "${businessType}" em "${location}". Mostrando estabelecimentos relevantes:`;
    }

    // Processamento final dos resultados
    const validatedResults = await validateLeads(allResults);
    const subscriptionPlan = subscription?.plan || "initial";
    const resultsToDisplay = selectResultsToDisplay(validatedResults, businessType, location, subscriptionPlan);

    console.log(`📊 Debug: Plan recebido: "${subscription?.plan}", Plan usado: "${subscriptionPlan}", Resultados: ${resultsToDisplay.length}`);
    
    const suggestions = generateAlternativeSearches(businessType, location);
    
    // ✅ SALVA NO CACHE com TODOS os resultados (27)
    const cacheEntry: CacheEntry = {
      allLeads: validatedResults,    // ← Todos os 27 resultados
      sources: [actualSource],
      executionTime: Date.now() - startTime,
      message,
      fallbackUsed,
      originalSearch: { businessType, location },
      suggestions
    };

    cache.set(cacheKey, { 
      data: cacheEntry, 
      timestamp: Date.now() 
    });

    // ✅ Retorna apenas os resultados filtrados (10)
    const result: LeadsResult = {
      success: true,
      leads: resultsToDisplay,
      sources: [actualSource],
      executionTime: Date.now() - startTime,
      message,
      fallbackUsed,
      originalSearch: { businessType, location },
      suggestions,
      fromCache: false
    };    

    console.log(`🎯 RESULTADO FINAL:`, {
      success: result.success,
      leadsCount: result.leads.length,
      leads: result.leads.map(l => ({ name: l.name, phone: l.phone, email: l.email })),
      sources: result.sources,
      fallbackUsed: result.fallbackUsed,
      message: result.message
    });

    return result;

  } catch (error) {
    console.error('💥 Erro na busca:', error);
    return getErrorFallback(searchTerm, cityName, startTime, error);
  } finally {
    clearTimeout(timeout);
  }
};

// Função auxiliar para tentar todos os servidores
const tryAllServers = async (
  query: { query: string; name: string },
  businessType: string,
  location: string,
  estado?: string
): Promise<Lead[]> => {
  const errors = [];
  const MAX_ATTEMPTS = 15; 
  
  for (let i = 0; i < Math.min(OVERPASS_SERVERS.length, MAX_ATTEMPTS); i++) {
    const server = OVERPASS_SERVERS[i];
    try {
      console.log(`🔄 Tentando ${query.name} no servidor ${server}`);
      const response = await fetchWithTimeout(
        server,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: `data=${encodeURIComponent(query.query)}`
        },
        25000 // 25 segundos timeout
      );

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const data = await response.json();
      const elements = data.elements || [];
      
      if (elements.length > 0) {
        console.log(`✅ Encontrados ${elements.length} resultados`);
        return processOSMElements(elements, businessType, location, estado);
      }
      
      return []; // Nenhum resultado encontrado

    } catch (error) {
      console.error(`❌ Falha no servidor ${server}:`, error);
      errors.push(error);
      await new Promise(resolve => setTimeout(resolve, 300)); // Pequena pausa
    }
  }
  
  console.log('⏩ Pulando para fallback após tentativas rápidas');
  return []; // Retorna vazio para cair no fallback mais rápido
};

// Funções auxiliares
const getIntelligentFallback = (businessType: string, location: string): Lead[] => {
  const filtered = filterFallbackByBusinessType([...BRAZIL_FALLBACK_CONTACTS], businessType);
  return filtered.slice(0, 3).map(contact => ({
    ...contact,
    source: "Base Geral"
  }));
};

const getErrorFallback = (searchTerm: string, cityName: string, startTime: number, error: unknown): LeadsResult => {
  const [businessType, location] = extractSearchParams(searchTerm, cityName);
  
  const fallbackLeads = shuffleArray([...BRAZIL_FALLBACK_CONTACTS], Date.now()).slice(0, 3);
  
  return {
    success: true,
    leads: fallbackLeads,
    executionTime: Date.now() - startTime,
    sources: ['Base Geral'],
    fallbackUsed: true,
    error: error instanceof Error ? error.message : "Erro desconhecido",
    message: "Mostrando estabelecimentos gerais devido a erro na busca",
    originalSearch: {
      businessType: searchTerm,
      location: cityName || 'Brasil'
    },
    suggestions: generateAlternativeSearches(businessType, location || 'Brasil')
  };
};

// Funções auxiliares usadas na searchBusinessLeads
const tryMainSearch = async (businessType: string, location: string, amenityTags: string[], estado?: string): Promise<Lead[]> => {
  const isHeavy = isHeavySearch(location, businessType, estado);
  
  // 1. Primeira tentativa - com filtro se for busca pesada
  const query = buildMainQuery(businessType, location, amenityTags, estado);
  
  try {
    console.log(`🔍 Primeira tentativa: ${query.name}`);
    const response = await fetchOverpass(query.query);
    const results = processOSMElements(response.elements, businessType, location, estado);
    
    // Se é busca pesada e retornou menos de 100 leads, refaz sem filtro
    if (isHeavy && results.length < 100) {
      console.log(`⚡ Busca pesada retornou apenas ${results.length} leads, refazendo sem filtro...`);
      
      // 2. Segunda tentativa - sem filtro
      const queryWithoutFilter = buildMainQueryWithoutFilter(businessType, location, amenityTags, estado);
      
      try {
        console.log(`🔄 Segunda tentativa: ${queryWithoutFilter.name}`);
        const responseWithoutFilter = await fetchOverpass(queryWithoutFilter.query);
        const resultsWithoutFilter = processOSMElements(responseWithoutFilter.elements, businessType, location, estado);
        
        console.log(`✅ Busca sem filtro retornou ${resultsWithoutFilter.length} leads`);
        return resultsWithoutFilter;
        
      } catch (errorWithoutFilter) {
        console.error('Erro na busca sem filtro:', errorWithoutFilter);
        // Retorna os resultados da primeira tentativa se a segunda falhar
        return results;
      }
    }
    
    return results;
    
  } catch (error) {
    console.error('Erro na busca principal:', error);
    return [];
  }
};

const buildMainQueryWithoutFilter = (businessType: string, location: string, amenityTags: string[], estado?: string): { query: string; name: string } => {
  const locationParts = location.split('-');
  let finalLocation = location;
  let finalEstado = estado;
  
  if (locationParts.length > 1 && locationParts[1].match(/^[A-Z]{2}$/)) {
    finalLocation = locationParts[0].trim();
    finalEstado = locationParts[1].trim();
  }

  if (finalEstado) {
    return buildHierarchicalQueryWithoutFilter(businessType, finalLocation, finalEstado, amenityTags);
  }

  // Busca normal sem filtro
  if (amenityTags && amenityTags.length > 0 && !amenityTags.includes('*')) {
    let query = `[out:json][timeout:300];
(
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"];
      way(area.search_area)[${key}="${value}"];
      relation(area.search_area)[${key}="${value}"];`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop];
      way(area.search_area)[shop];
      relation(area.search_area)[shop];`;
    } else {
      return `node(area.search_area)[amenity="${tag}"];
      way(area.search_area)[amenity="${tag}"];
      relation(area.search_area)[amenity="${tag}"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;
    
    return { query, name: `Busca específica SEM FILTRO: ${businessType} em ${finalLocation}` };
  }
  
  // Busca ampla sem filtro
  let query = `[out:json][timeout:300];
(
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="8"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="4"];
  rel["name"="${finalLocation}"]["type"="boundary"]["admin_level"="2"];
);
map_to_area -> .search_area;
(
  node(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  way(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
  relation(area.search_area)[~"^(shop|amenity|office|tourism|leisure|craft|industrial)$"~"."];
);
out body center qt 1000;`;

  return { query, name: `Busca ampla SEM FILTRO em ${finalLocation}` };
};

const buildHierarchicalQueryWithoutFilter = (
  businessType: string, 
  location: string, 
  estado: string, 
  amenityTags: string[]
): { query: string; name: string } => {
  
  const estadoNames: Record<string, string> = {
    'AC': 'Acre', 'AL': 'Alagoas', 'AP': 'Amapá', 'AM': 'Amazonas', 'BA': 'Bahia', 
    'CE': 'Ceará', 'DF': 'Distrito Federal', 'ES': 'Espírito Santo', 'GO': 'Goiás', 
    'MA': 'Maranhão', 'MT': 'Mato Grosso', 'MS': 'Mato Grosso do Sul', 'MG': 'Minas Gerais', 
    'PA': 'Pará', 'PB': 'Paraíba', 'PR': 'Paraná', 'PE': 'Pernambuco', 'PI': 'Piauí', 
    'RJ': 'Rio de Janeiro', 'RN': 'Rio Grande do Norte', 'RS': 'Rio Grande do Sul', 
    'RO': 'Rondônia', 'RR': 'Roraima', 'SC': 'Santa Catarina', 'SP': 'São Paulo', 
    'SE': 'Sergipe', 'TO': 'Tocantins'
  };

  const estadoName = estadoNames[estado] || estado;
  const isStateSearch = location.toLowerCase() === estadoName.toLowerCase() || 
                       location.toLowerCase() === estado.toLowerCase() ||
                       location.toLowerCase() === 'brasil';

  // SEM FILTRO de phone/email
  const phoneEmailFilter = '';

  if (isStateSearch) {
    const query = `[out:json][timeout:300];
rel["name"="${estadoName}"]["type"="boundary"]["admin_level"="4"];
map_to_area -> .search_area;

(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"];
      way(area.search_area)[${key}="${value}"];
      relation(area.search_area)[${key}="${value}"];`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop];
      way(area.search_area)[shop];
      relation(area.search_area)[shop];`;
    } else {
      return `node(area.search_area)[amenity="${tag}"];
      way(area.search_area)[amenity="${tag}"];
      relation(area.search_area)[amenity="${tag}"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

    return { query, name: `Busca estadual SEM FILTRO: ${businessType} em ${estadoName}` };
  }

  // Busca em cidade dentro do estado (sem filtro)
  const query = `[out:json][timeout:300];
rel["name"="${estadoName}"]["type"="boundary"]["admin_level"="4"];
map_to_area -> .state_area;

(
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  rel(area.state_area)["name"="${location}"]["type"="boundary"]["admin_level"="7"];
);
map_to_area -> .search_area;

(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.search_area)[${key}="${value}"];
      way(area.search_area)[${key}="${value}"];
      relation(area.search_area)[${key}="${value}"];`;
    } else if (tag === 'shop') {
      return `node(area.search_area)[shop];
      way(area.search_area)[shop];
      relation(area.search_area)[shop];`;
    } else {
      return `node(area.search_area)[amenity="${tag}"];
      way(area.search_area)[amenity="${tag}"];
      relation(area.search_area)[amenity="${tag}"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca hierárquica SEM FILTRO: ${businessType} em ${location} (${estado})`
  };
};

const tryLocationNameSearch = async (businessType: string, location: string, estado?: string): Promise<Lead[]> => {
  const query = `[out:json][timeout:90];
    (
      node["name"~"${location}",i][shop];      
    );
    out body center qt 500;`;
  
  try {
    const response = await fetchOverpass(query);
    return processOSMElements(response.elements, businessType, location, estado);
  } catch (error) {
    console.error('Erro na busca por nome da localidade:', error);
    return [];
  }
};

// Atualize a função tryNearbyCitiesSearch
const tryNearbyCitiesSearch = async (businessType: string, location: string, estado?: string): Promise<Lead[]> => {
  const isHeavy = isHeavySearch(location, businessType, estado);
  
  // 1. Primeira tentativa com filtro se for busca pesada
  const query = buildNearbyCitiesQuery(businessType, location, estado);
  
  try {
    console.log(`🔍 Primeira tentativa cidades vizinhas: ${query.name}`);
    const response = await fetchOverpass(query.query);
    const results = processOSMElements(response.elements, businessType, location, estado);
    
    // Se é busca pesada e retornou menos de 100 leads, refaz sem filtro
    if (isHeavy && results.length < 100) {
      console.log(`⚡ Busca pesada em cidades vizinhas retornou apenas ${results.length} leads, refazendo sem filtro...`);
      
      // 2. Segunda tentativa sem filtro
      const queryWithoutFilter = buildNearbyCitiesQueryWithoutFilter(businessType, location, estado);
      
      try {
        console.log(`🔄 Segunda tentativa cidades vizinhas: ${queryWithoutFilter.name}`);
        const responseWithoutFilter = await fetchOverpass(queryWithoutFilter.query);
        const resultsWithoutFilter = processOSMElements(responseWithoutFilter.elements, businessType, location, estado);
        
        console.log(`✅ Busca cidades vizinhas sem filtro retornou ${resultsWithoutFilter.length} leads`);
        return resultsWithoutFilter;
        
      } catch (errorWithoutFilter) {
        console.error('Erro na busca cidades vizinhas sem filtro:', errorWithoutFilter);
        return results;
      }
    }
    
    return results;
    
  } catch (error) {
    console.error('Erro na busca em cidades vizinhas:', error);
    return [];
  }
};

const buildNearbyCitiesQueryWithoutFilter = (businessType: string, location: string, estado?: string): { query: string; name: string } => {
  const amenityTags = getAmenityTags(businessType);
  
  // SEM FILTRO de phone/email
  const phoneEmailFilter = '';

  const query = `[out:json][timeout:300];
(
  rel["name"="${location}"]["type"="boundary"]["admin_level"="8"];
  node["name"="${location}"]["place"="village"];
);
map_to_area -> .main_area;

(
  rel(around.main_area:20000)["type"="boundary"]["admin_level"="8"];
);
map_to_area -> .neighbor_areas;

(
  ${amenityTags.map(tag => {
    if (tag.includes('=')) {
      const [key, value] = tag.split('=');
      return `node(area.neighbor_areas)[${key}="${value}"];
      way(area.neighbor_areas)[${key}="${value}"];`;
    } else if (tag === 'shop') {
      return `node(area.neighbor_areas)[shop];
      way(area.neighbor_areas)[shop];`;
    } else {
      return `node(area.neighbor_areas)[amenity="${tag}"];
      way(area.neighbor_areas)[amenity="${tag}"];`;
    }
  }).join('\n')}
);
out body center qt 1000;`;

  return {
    query,
    name: `Busca em cidades próximas SEM FILTRO a ${location}`
  };
};

// Adicione esta função para processar elementos com log informativo
const processOSMElements = (elements: any[], businessType: string, location: string, estado?: string): Lead[] => {
  if (!elements || elements.length === 0) {
    console.log('📭 Nenhum elemento encontrado no processamento');
    return [];
  }
  
  console.log(`📊 Processando ${elements.length} elementos de OSM`);
  
  const leads = elements
    .map((element: any) => mapElementToLead(element, businessType, location, estado))
    .filter((lead: Lead) => lead.name && lead.name !== 'Nome não encontrado');
  
  console.log(`✅ ${leads.length} leads válidos após filtro`);
  return leads;
};

// Adicione esta função para ajudar a ordenar os resultados
const sortFallbackResults = (results: Lead[]): Lead[] => {
  return results.sort((a, b) => {
    // Prioriza contatos com telefone e email reais
    if (a.hasRealPhone && a.hasRealEmail && !(b.hasRealPhone && b.hasRealEmail)) return -1;
    if (!(a.hasRealPhone && a.hasRealEmail) && b.hasRealPhone && b.hasRealEmail) return 1;
    
    // Depois prioriza contatos com telefone real
    if (a.hasRealPhone && !b.hasRealPhone) return -1;
    if (!a.hasRealPhone && b.hasRealPhone) return 1;
    
    // Depois prioriza contatos com email real
    if (a.hasRealEmail && !b.hasRealEmail) return -1;
    if (!a.hasRealEmail && b.hasRealEmail) return 1;
    
    // Ordenação aleatória para o restante
    return Math.random() > 0.5 ? 1 : -1;
  });
};

const extractSearchParams = (searchTerm: string, cityName?: string): [string, string] => {
  // ✅ MELHORIA: Preserva melhor o formato com hífen
  // Caso especial para termos com hífen (Ituiutaba-MG, Campinas-SP)
  const hyphenPattern = /(.+)\s+(em|no|na|nos|nas)\s+([A-Za-zÀ-ú\s]+-\s*[A-Z]{2})/i;
  const hyphenMatch = searchTerm.match(hyphenPattern);
  
  if (hyphenMatch) {
    const businessType = hyphenMatch[1].trim();
    const location = hyphenMatch[3].trim();
    console.log(`🔍 Detectado padrão com hífen: "${businessType}" em "${location}"`);
    return [businessType, location];
  }

  // Caso já tenha a cidade definida
  if (cityName?.trim()) {
    return [searchTerm.trim(), cityName.trim()];
  }

  // ✅ NOVO: Padrão para detectar cidade-estado no final da frase
  const cityStatePattern = /(.+)\s+(em|no|na|nos|nas)\s+([A-Za-zÀ-ú\s]+[-]\s*[A-Z]{2})$/i;
  const cityStateMatch = searchTerm.match(cityStatePattern);
  
  if (cityStateMatch) {
    const businessType = cityStateMatch[1].trim();
    const location = cityStateMatch[3].trim();
    console.log(`🔍 Detectado cidade-estado: "${businessType}" em "${location}"`);
    return [businessType, location];
  }

  // Padrão para localizações com estado separado por vírgula ou espaço
  const statePattern = /(.+)\s+(em|no|na|nos|nas)\s+([^,]+)(,\s*([A-Z]{2})?)?/i;
  const stateMatch = searchTerm.match(statePattern);

  if (stateMatch) {
    const location = stateMatch[3].trim() + (stateMatch[5] ? `-${stateMatch[5].trim()}` : '');
    return [stateMatch[1].trim(), location];
  }

  // Se não encontrar padrões específicos, faz a divisão tradicional
  const locationPrepositions = new Set(['em', 'no', 'na', 'nos', 'nas']);
  const words = searchTerm.split(/\s+/);
  let businessTypeParts = [];
  let locationParts = [];
  let foundPreposition = false;

  for (const word of words) {
    if (!foundPreposition && locationPrepositions.has(word.toLowerCase())) {
      foundPreposition = true;
      continue;
    }

    if (foundPreposition) {
      locationParts.push(word);
    } else {
      businessTypeParts.push(word);
    }
  }

  const businessType = businessTypeParts.join(' ').trim();
  const location = locationParts.join(' ').trim();

  // Se não encontrou preposição, assume que o último termo é a localização
  if (!foundPreposition && words.length > 1) {
    return [words.slice(0, -1).join(' ').trim(), words[words.length - 1].trim()];
  }

  return [businessType || searchTerm, location || ''];
};

export const validateLeads = async (leads: Lead[]): Promise<Lead[]> => {
  const filtered = leads.filter(lead => 
    lead.name && 
    lead.name !== 'Nome não encontrado' && 
    (lead.phone || lead.email || lead.address)
  );
  return sortLeadsByPriority(filtered);
};

const decrementRequestCounter = async (userId: string, currentCount: number): Promise<boolean> => {
  if (currentCount <= 0) {
    console.log('❌ currentCount <= 0:', currentCount);
    return false;
  }
  
  try {
    console.log('🔄 Tentando decrementar:', { userId, currentCount, newCount: currentCount - 1 });
    
    const { data, error: updateError } = await supabase
      .from('subscriptions')
      .update({ request_count: currentCount - 1 })
      .eq('user_id', userId)
      .select(); // ✅ Adicione .select() para ver o resultado

    if (updateError) {
      console.error('❌ Error updating request count:', updateError);
      return false;
    }
    
    console.log('✅ Request count atualizado:', data);
    return true;
  } catch (error) {
    console.error('❌ Error in decrementRequestCounter:', error);
    return false;
  }
};

export type { Lead, LeadsResult };